import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton, InputAdornment, LinearProgress, Paper, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EmojiFoodBeverageRoundedIcon from '@mui/icons-material/EmojiFoodBeverageRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { formatMetric } from './analysis/analysis.shared';
import {
  ManualMealPlanAnalysisDashboard,
  type DayAnalysisSnapshot,
  type FatBreakdownRow,
  type NutrientVsRdaBar,
} from '@/components/meal-planning/manual/ManualMealPlanAnalysisDashboard';
import type { RowTone } from './analysis/NutritionReportSectionTable';
import { getSuggestedRdaRowTone, suggestedRdaToneStyles } from './analysis/suggestedRdaTone';
import { ClientDetailsV2AddDayDialog, type AddDayPayload } from './ClientDetailsV2AddDayDialog';
import { ClientDetailsV2CopyDayDialog } from './ClientDetailsV2CopyDayDialog';
import { SaveAsTemplateModal, type SaveAsTemplatePayload } from '@/components/shared/SaveAsTemplateModal';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import trashIcon from '@/assets/images/trash-bin-trash.svg';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';
import { resolveRangeMidpoint } from '@/utils/nutritionGoals';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import NutritionalAnalysisPage from '@/pages/manual/NutritionalAnalysisPage';
import GenericMealPlanNutritionPage from '@/pages/meal-plan/GenericMealPlanNutritionPage';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';
import {
  fetchNutritionalAnalysisDayData,
  type NutritionalAnalysisLoadedData,
} from '@/pages/manual/nutritionalAnalysis/nutritionalAnalysisDataLoader';
import {
  buildManualNutritionAnalysisSnapshot,
  getMicronutrientTrafficLightColor,
  getTrafficLightColor,
  type ManualNutritionDrvContext,
  rowMacroTotals,
} from '@/pages/manual/nutritionalAnalysis/nutritionalAnalysisPure';
import RecipeDetailsModal from '@/components/meal-planning/RecipeDetailsModal';
import { MealPlanNotesControl } from '@/components/meal-planning/MealPlanNotesControl';
import { MealPlanRestrictionBadges } from '@/components/shared/MealPlanRestrictionBadges';
import { Tag } from '@/components/ui/tag';
import type {
  FinalizedMeal,
  FinalizedMealDay,
  FinalizedMealPlanData,
  RecipeServingSize,
} from '@/types/meal-plan';
import type { Client, ClientGoal } from '@/types/client';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { buildMealPlanRestrictionWarning } from '@/utils/meal-plan-allergen-warnings';
import { getDrvPrimaryTypes } from '@/lib/api/drv';
import {
  trackMealPlanDayCopyClicked,
  trackMealPlanDetailedReportClicked,
  trackMealPlanTemplateSaveClicked,
} from '@/lib/analytics/trackers/mealPlanActions';
import {
  listMealPlanTemplates,
  saveDraftMealPlanAsTemplate,
  saveFinalizedMealPlanAsTemplate,
  useMealPlanTemplate,
  updateGenericMealPlanItem,
  type MealPlanTemplateListPagination,
  type MealPlanTemplateSummary,
  type UpdateMealPlanNotesRequest,
} from '@/lib/api/mealPlans';

/** API may attach flat dietary row + legacy ranges alongside nested `clientGoals`. */
type ClientWithProfileTargets = Client & {
  clientGoal?: ClientGoal | null;
  proteinGoalMin?: number;
  proteinGoalMax?: number;
  carbsGoalMin?: number;
  carbsGoalMax?: number;
  fatGoalMin?: number;
  fatGoalMax?: number;
  macrosRanges?: {
    protein?: { min?: number | null; max?: number | null };
    carbs?: { min?: number | null; max?: number | null };
    fat?: { min?: number | null; max?: number | null };
  };
};

function readPositiveMacroNumber(v: unknown): number | undefined {
  const n = typeof v === 'string' && String(v).trim() !== '' ? Number(v) : Number(v);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  rectSwappingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableDayWrapperProps {
  id: string;
  day: number;
  disabled?: boolean;
  children: (props: { setNodeRef: any; attributes: any; listeners: any; style: any; isDragging: boolean }) => React.ReactNode;
}

function SortableDayWrapper({ id, day, disabled, children }: SortableDayWrapperProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { type: 'Day', day }, disabled });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 0 : 1,
    opacity: isDragging ? 0 : 1, // Completely hide the original item, leaving only the DragOverlay visible and a blank space
  };

  return children({ setNodeRef, attributes, listeners, style, isDragging });
}

interface SortableMealWrapperProps {
  id: string;
  dayNumber: number;
  mealKey: string;
  disabled?: boolean;
  children: (props: { setNodeRef: any; attributes: any; listeners: any; style: any; isDragging: boolean }) => React.ReactNode;
}

function SortableMealWrapper({ id, dayNumber, mealKey, disabled, children }: SortableMealWrapperProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { type: 'Meal', dayNumber, mealKey }, disabled });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 0 : 1,
    opacity: isDragging ? 0 : 1, // Completely hide the original item, leaving only the DragOverlay visible and a blank space
  };

  return children({ setNodeRef, attributes, listeners, style, isDragging });
}

interface ClientDetailsV2MealPlanBoardViewProps {
  planName: string;
  planDurationLabel: string;
  data: FinalizedMealPlanData;
  /** Daily macro/calorie targets prefer meal plan `targetDaily` from API; otherwise client EER / goals. */
  client?: Client | null;
  onBack: () => void;
  onEditDay?: (dayNumber: number) => void;
  onAddDay?: (payload: AddDayPayload) => void;
  /** Copy meals from one source day onto one or more target days. */
  onCopyDay?: (payload: { sourceDay: number; targetDays: number[] }) => Promise<void>;
  isCopyingDay?: boolean;
  onSaveDayName: (dayNumber: number, dayName: string | null) => Promise<boolean>;
  isSavingDayName: boolean;
  onDeleteDay: (dayNumber: number) => Promise<boolean>;
  isDeletingDay: boolean;
  onReorderDays: (dayOrder: number[]) => Promise<boolean>;
  isReorderingDays: boolean;
  onReorderMealsInDay: (
    dayNumber: number,
    orderedMealOrders: number[],
    mealSwap?: {
      firstMealName: string;
      secondMealName: string;
      firstMealOrder?: number;
      secondMealOrder?: number;
    }
  ) => Promise<boolean>;
  isReorderingMeals: boolean;
  /** Refetch meal plan in parent after child (nutrition) mutates draft on server */
  onMealPlanUpdated?: () => void | Promise<void>;
  hidePlanTabs?: boolean;
  /** Read-only board mode for templates the current viewer cannot modify. */
  readOnly?: boolean;
  editDayLabel?: string;
  /** Hide day creation affordances for views that must stay fixed length, like meal-slot templates. */
  hideAddDayUi?: boolean;
  /** Force the board shell to fill the available viewport height. */
  fillViewport?: boolean;
  planMode?: 'manual' | 'generic';
  planCategory?: 'manual' | 'auto' | 'ai' | 'generic';
  /** When true with generic mode, hides the header "+ Add Meal Plan" next to Export (e.g. `/templates/:id/view?generic=true`). */
  hideGenericToolbarAddMeal?: boolean;
  onExportPdf?: () => void;
  isExportingPdf?: boolean;
  /** Finalize a manual draft. When provided and the plan is a draft, a "Finalise Plan" button is shown. */
  onFinalize?: () => void | Promise<void>;
  isFinalizing?: boolean;
  /** Practitioner notes at plan / day / meal / item levels via PUT /meal-plans/{id}/notes. */
  onSaveMealPlanNotes?: (request: UpdateMealPlanNotesRequest) => Promise<boolean>;
  isSavingMealPlanNotes?: boolean;
}

type MealEntry = {
  key: string;
  label: string;
  /** Exact mealName from API GET — use for PUT notes. */
  mealName: string;
  notes?: string | null;
  slotOrder: number;
  timeLabel: string;
  recipes: FinalizedMeal[];
  selectedOptionGroupId?: string | null;
};

type RecipeDisplayRow = {
  image: string;
  title: string;
  detailLabel: string;
  caloriesLabel: string;
  recipeId: string;
  itemId?: string;
  notes?: string | null;
  recipeSource?: 'spoonacular' | 'edamam' | 'internal';
  isRecipe: boolean;
  defaultServingId?: string | null;
  defaultServingGrams?: number | null;
  selectedServingId?: string | null;
  selectedServingGrams?: number | null;
  servingSizes?: RecipeServingSize[];
  optionGroupId?: string | null;
  optionGroupLabel?: string | null;
};

type MacroMetricKey = 'protein' | 'carbs' | 'fat' | 'fiber';
type FatBreakdownMetricKey = 'mufa' | 'pufa' | 'saturatedFat' | 'transFat';

type MealDragPayload = {
  dayNumber: number;
  mealKey: string;
};

type DayDragPayload = {
  type: 'day-reorder';
  dayNumber: number;
};

type GenericTemplateOption = {
  id: string;
  name: string;
  detail: string;
  kcal: number;
  allergens?: string[];
  intoleranceTags?: string[];
  tags: Array<{
    label: string;
    bg: string;
    color: string;
  }>;
};

const TEMPLATE_PICKER_PAGE_SIZE = 8;

const mealOrder: Record<string, number> = {
  'pre-breakfast': 0,
  breakfast: 1,
  'mid-morning-snack': 2,
  snack: 3,
  lunch: 4,
  'afternoon-snack': 5,
  dinner: 6,
};

const mealTimeFallback: Record<string, string> = {
  'pre-breakfast': '06:30 AM',
  breakfast: '07:30 AM',
  'mid-morning-snack': '10:30 AM',
  snack: '07:30 PM',
  lunch: '01:30 PM',
  'afternoon-snack': '05:00 PM',
  dinner: '08:30 PM',
};

const mealColorScale = ['#f59e0b', '#2fb0d4', '#2563eb', '#06b6d4', '#10b981', '#ef4444', '#8b5cf6'];
/** Matches Figma “Calories by Meal” dots (orange / teal / blue). */
const mealAccentByKey: Record<string, string> = {
  'pre-breakfast': '#f59e0b',
  breakfast: '#2ec3b6',
  'mid-morning-snack': '#3f9fee',
  snack: '#3f9fee',
  lunch: '#f59e0b',
  'afternoon-snack': '#2ec3b6',
  dinner: '#3f9fee',
};

function readTemplateCalories(plan: MealPlanTemplateSummary): number {
  const source = plan as Record<string, unknown>;
  const totals =
    source.totals && typeof source.totals === 'object'
      ? (source.totals as Record<string, unknown>)
      : null;
  const value = source.calories ?? source.totalCalories ?? totals?.calories;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function toGenericTemplateOption(plan: MealPlanTemplateSummary): GenericTemplateOption {
  const source = plan as Record<string, unknown>;
  const mealSlots = source.mealSlots;
  const days = source.days;
  const mealCount = Array.isArray(mealSlots)
    ? mealSlots.length
    : Array.isArray(days)
      ? days.reduce((count, day) => {
        if (!day || typeof day !== 'object') return count;
        const dayMeals = (day as Record<string, unknown>).meals;
        return count + (Array.isArray(dayMeals) ? dayMeals.length : 0);
      }, 0)
      : 0;
  const tags = Array.isArray(plan.clinicalTags) ? plan.clinicalTags : Array.isArray(plan.tags) ? plan.tags : [];
  return {
    id: String(plan.id),
    name: plan.templateName?.trim() || plan.name?.trim() || 'Cohort Meal Plan',
    detail: `${mealCount} ${mealCount === 1 ? 'meal' : 'meals'}`,
    kcal: readTemplateCalories(plan),
    allergens: Array.isArray(plan.allergens) ? plan.allergens : [],
    intoleranceTags: Array.isArray(plan.intoleranceTags) ? plan.intoleranceTags : [],
    tags: tags.map((label) => ({
      label: formatUnderscoreDisplayLabel(label),
      bg: '#eceff3',
      color: '#667085',
    })),
  };
}

function readTemplateTotalCount(pagination?: MealPlanTemplateListPagination): number {
  if (!pagination) return 0;
  const rawTotal = pagination.totalCount ?? pagination.totalItems ?? pagination.total ?? pagination.count;
  const parsed = Number(rawTotal);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function readTemplateTotalPages(pagination?: MealPlanTemplateListPagination): number {
  if (!pagination) return 1;
  const rawPages = pagination.totalPages ?? pagination.total_pages ?? pagination.pageCount ?? pagination.pages;
  const parsedPages = Number(rawPages);
  if (Number.isFinite(parsedPages) && parsedPages > 0) {
    return Math.floor(parsedPages);
  }

  const totalCount = readTemplateTotalCount(pagination);
  const perPageRaw = pagination.pageSize ?? pagination.limit ?? pagination.perPage;
  const perPage = Number(perPageRaw);
  if (totalCount > 0 && Number.isFinite(perPage) && perPage > 0) {
    return Math.max(1, Math.ceil(totalCount / perPage));
  }
  return 1;
}

function toMealKey(value: string): string {
  return value.toLowerCase().replace(/\s+/g, '-');
}

function toMealLabel(value: string): string {
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getMealRecipes(mealData: unknown): FinalizedMeal[] {
  if (!mealData || typeof mealData !== 'object') return [];
  const typedMeal = mealData as FinalizedMeal & { recipes?: FinalizedMeal[]; items?: FinalizedMeal[] };
  if (Array.isArray(typedMeal.items) && typedMeal.items.length) {
    return typedMeal.items.map((item) => {
      const record = item as FinalizedMeal & { name?: string };
      return {
        ...record,
        recipeName: record.recipeName ?? record.name,
      };
    });
  }
  if (Array.isArray(typedMeal.recipes) && typedMeal.recipes.length) return typedMeal.recipes;
  if (typedMeal.recipeName || typedMeal.recipeId) return [typedMeal];
  return [];
}

function getMealTimeLabel(key: string, mealData: unknown): string {
  if (!mealData || typeof mealData !== 'object') return mealTimeFallback[key] ?? '--';
  const typedMeal = mealData as { timeSlot?: string; mealTime?: string; time?: string };
  const fromData = typedMeal.timeSlot || typedMeal.mealTime || typedMeal.time || '';
  return fromData.trim() || mealTimeFallback[key] || '--';
}

function getMealSlotOrder(key: string, mealData: unknown): number {
  const fallback = mealOrder[toMealKey(key)] ?? 999;
  if (!mealData || typeof mealData !== 'object') return fallback;

  const typedMeal = mealData as {
    mealOrder?: number;
    sortOrder?: number;
    order?: number;
    recipes?: Array<{ mealOrder?: number; sortOrder?: number; order?: number }>;
  };

  if (typeof typedMeal.mealOrder === 'number' && typedMeal.mealOrder > 0) return typedMeal.mealOrder;
  if (typeof typedMeal.sortOrder === 'number' && typedMeal.sortOrder > 0) return typedMeal.sortOrder;
  if (typeof typedMeal.order === 'number' && typedMeal.order > 0) return typedMeal.order;

  const firstOrderedRecipe = typedMeal.recipes?.find((recipe) => {
    return (
      (typeof recipe.mealOrder === 'number' && recipe.mealOrder > 0) ||
      (typeof recipe.sortOrder === 'number' && recipe.sortOrder > 0) ||
      (typeof recipe.order === 'number' && recipe.order > 0)
    );
  });

  if (firstOrderedRecipe?.mealOrder && firstOrderedRecipe.mealOrder > 0) return firstOrderedRecipe.mealOrder;
  if (firstOrderedRecipe?.sortOrder && firstOrderedRecipe.sortOrder > 0) return firstOrderedRecipe.sortOrder;
  if (firstOrderedRecipe?.order && firstOrderedRecipe.order > 0) return firstOrderedRecipe.order;

  return fallback;
}

function getMealEntries(day: FinalizedMealDay): MealEntry[] {
  const entries = Object.entries(day.meals || {}).map(([key, mealData]) => {
    const mealRecord = mealData && typeof mealData === 'object' && !Array.isArray(mealData)
      ? mealData as Record<string, unknown>
      : {};
    const apiMealName = String(mealRecord.mealName ?? key).trim() || key;
    return {
      key,
      label: toMealLabel(key),
      mealName: apiMealName,
      notes: typeof mealRecord.notes === 'string'
        ? mealRecord.notes
        : mealRecord.notes === null
          ? null
          : undefined,
      slotOrder: getMealSlotOrder(key, mealData),
      timeLabel: getMealTimeLabel(toMealKey(key), mealData),
      recipes: getMealRecipes(mealData),
      selectedOptionGroupId: typeof mealRecord.selectedOptionGroupId === 'string' ? mealRecord.selectedOptionGroupId : null,
    };
  });

  return entries.sort((a, b) => {
    if (a.slotOrder !== b.slotOrder) return a.slotOrder - b.slotOrder;
    const fallbackA = mealOrder[toMealKey(a.key)] ?? 999;
    const fallbackB = mealOrder[toMealKey(b.key)] ?? 999;
    return fallbackA - fallbackB;
  });
}

function getRecipeCalories(recipe: FinalizedMeal): number {
  const macroCalories = recipe.nutrition?.macros?.calories;
  if (typeof macroCalories === 'number' && Number.isFinite(macroCalories)) return macroCalories;
  const flatCalories = recipe.nutrition?.calories;
  if (typeof flatCalories === 'number' && Number.isFinite(flatCalories)) return flatCalories;
  return 0;
}

function getProgressValue(value: number, target: number): number {
  if (!target || target <= 0) return 0;
  return Math.max(0, Math.min(100, (value / target) * 100));
}

/** Intake vs goal for Intake vs Goals bars (not capped at 100%). */
function nutrientVsGoalPercent(value: number, target: number): number {
  if (!target || target <= 0) return 0;
  const raw = (value / target) * 100;
  if (!Number.isFinite(raw)) return 0;
  return Math.max(0, raw);
}

function mealPlanTargetDailyHasNumericGoals(td: FinalizedMealPlanData['targetDaily'] | undefined): boolean {
  if (!td || typeof td !== 'object') return false;
  return (
    (typeof td.calories === 'number' && Number.isFinite(td.calories) && td.calories > 0)
    || (typeof td.proteinMax === 'number' && Number.isFinite(td.proteinMax) && td.proteinMax > 0)
    || (typeof td.carbsMax === 'number' && Number.isFinite(td.carbsMax) && td.carbsMax > 0)
    || (typeof td.fatMax === 'number' && Number.isFinite(td.fatMax) && td.fatMax > 0)
  );
}

/** Daily kcal target from client record: clinical/final target wins, then legacy profile fallbacks. */
function resolveClientEerDailyKcal(client: Client | null | undefined): number | undefined {
  if (!client) return undefined;
  const c = client as ClientWithProfileTargets;
  const candidates = [resolveClientTargetCalories(client), c.eerOverride, c.clientGoal?.eerGoalCalories, c.eerCalories, c.eer];
  for (const v of candidates) {
    const n = typeof v === 'string' && String(v).trim() !== '' ? Number(v) : Number(v);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
  }
  return undefined;
}

/** Macro targets from GET client (nested `clientGoals`, flat `clientGoal`, legacy fields, `macrosRanges`). */
function resolveClientProfileMacroTargets(client: Client | null | undefined): {
  proteinExact?: number;
  proteinMin?: number;
  proteinMax?: number;
  carbsExact?: number;
  carbsMin?: number;
  carbsMax?: number;
  fatExact?: number;
  fatMin?: number;
  fatMax?: number;
} {
  if (!client) return {};
  const c = client as ClientWithProfileTargets;
  const g = c.clientGoals;
  const nestProtein = readPositiveMacroNumber(g?.protein?.grams?.value ?? g?.protein?.value);
  const nestCarbs = readPositiveMacroNumber(g?.carbohydrates?.grams?.value);
  const nestFat = readPositiveMacroNumber(g?.totalFat?.grams?.value ?? g?.totalFat?.value);
  const mr = c.macrosRanges;
  const flat = c.clientGoal;

  return {
    proteinExact: nestProtein,
    proteinMin:
      nestProtein ??
      readPositiveMacroNumber(flat?.proteinGoalMin) ??
      readPositiveMacroNumber(c.proteinGoalMin) ??
      readPositiveMacroNumber(mr?.protein?.min),
    proteinMax:
      nestProtein ??
      readPositiveMacroNumber(flat?.proteinGoalMax) ??
      readPositiveMacroNumber(c.proteinGoalMax) ??
      readPositiveMacroNumber(mr?.protein?.max),
    carbsExact: nestCarbs,
    carbsMin:
      nestCarbs ??
      readPositiveMacroNumber(flat?.carbsGoalMin) ??
      readPositiveMacroNumber(c.carbsGoalMin) ??
      readPositiveMacroNumber(mr?.carbs?.min),
    carbsMax:
      nestCarbs ??
      readPositiveMacroNumber(flat?.carbsGoalMax) ??
      readPositiveMacroNumber(c.carbsGoalMax) ??
      readPositiveMacroNumber(mr?.carbs?.max),
    fatExact: nestFat,
    fatMin:
      nestFat ??
      readPositiveMacroNumber(flat?.fatGoalMin) ??
      readPositiveMacroNumber(c.fatGoalMin) ??
      readPositiveMacroNumber(mr?.fat?.min),
    fatMax:
      nestFat ??
      readPositiveMacroNumber(flat?.fatGoalMax) ??
      readPositiveMacroNumber(c.fatGoalMax) ??
      readPositiveMacroNumber(mr?.fat?.max),
  };
}

/** Trend + bar colors: same rules as Suggested RDA (`getSuggestedRdaRowTone`, ±10% / ±15% bands). */
function getMacroTrendFromSuggestedRda(value: number, target: number): {
  label: string;
  color: string;
  barColor: string;
  Icon: React.ComponentType<{ sx?: Record<string, unknown> }>;
} {
  const tone: RowTone = target <= 0 ? 'na' : getSuggestedRdaRowTone(value, target, false);
  const styles = suggestedRdaToneStyles[tone];
  const progress = target > 0 ? (value / target) * 100 : null;
  const label =
    progress == null || !Number.isFinite(progress)
      ? '—'
      : `${formatMetric(Math.max(0, Math.min(999, progress)), 0)}%`;

  const Icon =
    tone === 'deficient'
      ? TrendingDownRoundedIcon
      : tone === 'excess'
        ? TrendingUpRoundedIcon
        : TrendingFlatRoundedIcon;

  return {
    label,
    color: styles.bar,
    barColor: styles.bar,
    Icon,
  };
}

function getMealAccent(mealKey: string, index: number): string {
  const normalized = toMealKey(mealKey);
  return mealAccentByKey[normalized] || mealColorScale[index % mealColorScale.length];
}

function toAnalysisMealLabel(mealKey: string): string {
  const normalized = toMealKey(mealKey);
  if (normalized === 'pre-breakfast') return 'Pre-Breakfast';
  if (normalized === 'mid-morning-snack') return 'Snack 1';
  if (normalized === 'afternoon-snack') return 'Snack 2';
  if (normalized === 'snack') return 'Snack';
  return toMealLabel(mealKey);
}

function getNumericValue(source: Record<string, unknown> | undefined, keys: string[]): number | null {
  if (!source) return null;

  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

function getRecipeMacro(recipe: FinalizedMeal, key: MacroMetricKey): number {
  const aliasesByKey: Record<MacroMetricKey, string[]> = {
    protein: ['protein'],
    carbs: ['carbs', 'carbohydrate'],
    fat: ['fat', 'total_fat'],
    fiber: ['fiber', 'dietary_fibre'],
  };

  const aliases = aliasesByKey[key];
  const nutritionRecord = recipe.nutrition as Record<string, unknown> | undefined;
  const macroRecord = (nutritionRecord?.macros as Record<string, unknown> | undefined) || undefined;

  const macroValue = getNumericValue(macroRecord, aliases);
  if (macroValue !== null) return macroValue;

  const flatValue = getNumericValue(nutritionRecord, aliases);
  if (flatValue !== null) return flatValue;

  return 0;
}

const fatBreakdownAliasesByKey: Record<FatBreakdownMetricKey, string[]> = {
  mufa: ['mufa', 'monounsaturated_fat', 'monounsaturatedFat', 'monounsaturated'],
  pufa: ['pufa', 'polyunsaturated_fat', 'polyunsaturatedFat', 'polyunsaturated'],
  saturatedFat: ['saturated_fat', 'saturatedFat', 'saturated'],
  transFat: ['trans_fat', 'transFat', 'trans'],
};

const fatBreakdownKeys: FatBreakdownMetricKey[] = ['mufa', 'pufa', 'saturatedFat', 'transFat'];

function getRecipeNutrientValue(recipe: FinalizedMeal, aliases: string[]): number | null {
  const nutritionRecord = recipe.nutrition as Record<string, unknown> | undefined;
  const macroRecord = (nutritionRecord?.macros as Record<string, unknown> | undefined) || undefined;

  const macroValue = getNumericValue(macroRecord, aliases);
  if (macroValue !== null) return macroValue;

  const flatValue = getNumericValue(nutritionRecord, aliases);
  if (flatValue !== null) return flatValue;

  return null;
}

function getMealSlotNutrientTotal(mealData: unknown, recipes: FinalizedMeal[], aliases: string[]): number | null {
  let fromRecipes = 0;
  let hasRecipeValue = false;

  recipes.forEach((recipe) => {
    const value = getRecipeNutrientValue(recipe, aliases);
    if (value === null) return;
    hasRecipeValue = true;
    fromRecipes += value;
  });

  if (hasRecipeValue) return fromRecipes;
  if (mealData && typeof mealData === 'object') {
    return getRecipeNutrientValue(mealData as FinalizedMeal, aliases);
  }

  return null;
}

function getDayFatBreakdownTotalsFromMeals(
  day: FinalizedMealDay,
  mealEntries: MealEntry[],
): Record<FatBreakdownMetricKey, number | null> {
  const sums: Record<FatBreakdownMetricKey, number> = {
    mufa: 0,
    pufa: 0,
    saturatedFat: 0,
    transFat: 0,
  };
  const hasValue: Record<FatBreakdownMetricKey, boolean> = {
    mufa: false,
    pufa: false,
    saturatedFat: false,
    transFat: false,
  };

  mealEntries.forEach((entry) => {
    const mealData = day.meals?.[entry.key];
    fatBreakdownKeys.forEach((key) => {
      const value = getMealSlotNutrientTotal(mealData, entry.recipes, fatBreakdownAliasesByKey[key]);
      if (value === null) return;
      hasValue[key] = true;
      sums[key] += Math.max(0, value);
    });
  });

  return fatBreakdownKeys.reduce<Record<FatBreakdownMetricKey, number | null>>((acc, key) => {
    acc[key] = hasValue[key] ? Math.max(0, Number(sums[key].toFixed(1))) : null;
    return acc;
  }, {
    mufa: null,
    pufa: null,
    saturatedFat: null,
    transFat: null,
  });
}

/** Sum line-item macros; if zero (e.g. missing per-item nutrition), use the meal slot aggregate. */
function getMealSlotCalories(mealData: unknown, recipes: FinalizedMeal[]): number {
  const fromRecipes = recipes.reduce((sum, recipe) => sum + getRecipeCalories(recipe), 0);
  if (fromRecipes > 0) return fromRecipes;
  if (mealData && typeof mealData === 'object') {
    return getRecipeCalories(mealData as FinalizedMeal);
  }
  return 0;
}

function getMealSlotMacro(mealData: unknown, recipes: FinalizedMeal[], key: MacroMetricKey): number {
  const fromRecipes = recipes.reduce((sum, recipe) => sum + getRecipeMacro(recipe, key), 0);
  if (fromRecipes > 0) return fromRecipes;
  if (mealData && typeof mealData === 'object') {
    return getRecipeMacro(mealData as FinalizedMeal, key);
  }
  return 0;
}

type MealRowMetadataSource = FinalizedMeal & {
  itemType?: unknown;
  amount?: unknown;
  servingId?: unknown;
  amountGrams?: unknown;
  servingGrams?: unknown;
  selectedServingGrams?: unknown;
  servingSizes?: unknown;
  selectedServingId?: unknown;
  defaultServingId?: unknown;
};

function toPositiveNumber(value: unknown): number | null {
  const parsed = typeof value === 'string' && value.trim().length > 0 ? Number(value) : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

function normalizeServingId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length ? trimmed : null;
}

function formatDisplayQuantity(value: number): string {
  return roundNutritionDisplay(Math.max(0, value));
}

function formatDisplayUnit(unit: string): string {
  const normalized = unit.trim().replace(/[_-]+/g, ' ');
  if (!normalized.length) return '';
  const lower = normalized.toLowerCase();
  if (lower === 'g' || lower === 'gram' || lower === 'grams') return 'gm';
  if (lower === 'l') return 'L';
  return normalized;
}

function formatServingSizeLabel(value: string): string {
  const normalized = value.trim().replace(/[_-]+/g, ' ');
  if (!normalized.length) return '';
  return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizeServingSizes(raw: unknown): RecipeServingSize[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const record = entry as Record<string, unknown>;
      const servingId = typeof record.servingId === 'string' ? record.servingId.trim() : '';
      if (!servingId.length) return null;
      const grams = toPositiveNumber(record.grams) ?? 0;
      const label = typeof record.label === 'string' && record.label.trim().length > 0
        ? record.label.trim()
        : undefined;
      return {
        servingId,
        grams,
        ...(label ? { label } : {}),
        isDefault: Boolean(record.isDefault),
      } satisfies RecipeServingSize;
    })
    .filter((entry): entry is RecipeServingSize => entry !== null);
}

function resolveSelectedServingSize(
  recipe: MealRowMetadataSource,
  servingSizes: RecipeServingSize[],
): RecipeServingSize | null {
  if (!servingSizes.length) return null;

  const selectedServingId = normalizeServingId(recipe.selectedServingId);
  const defaultServingId = normalizeServingId(recipe.defaultServingId);

  if (selectedServingId) {
    const selected = servingSizes.find((entry) => normalizeServingId(entry.servingId) === selectedServingId);
    if (selected) return selected;
  }

  if (defaultServingId) {
    const fallback = servingSizes.find((entry) => normalizeServingId(entry.servingId) === defaultServingId);
    if (fallback) return fallback;
  }

  return servingSizes.find((entry) => entry.isDefault) ?? servingSizes[0] ?? null;
}

function buildRowDetailSegments(recipe: FinalizedMeal): string[] {
  const record = recipe as MealRowMetadataSource;
  const itemType = typeof record.itemType === 'string' ? record.itemType.trim().toLowerCase() : '';
  const recipeId = String(recipe.recipeId ?? '').trim();
  const isRecipe = itemType ? itemType === 'recipe' : recipeId.length > 0;

  if (isRecipe) {
    const servings = toPositiveNumber(recipe.nutritionServings ?? recipe.servings) ?? 1;
    const servingSizes = normalizeServingSizes(record.servingSizes);
    const selectedServingSize = resolveSelectedServingSize(record, servingSizes);
    const selectedServingId = normalizeServingId(record.selectedServingId)
      ?? normalizeServingId(selectedServingSize?.servingId)
      ?? normalizeServingId(record.defaultServingId);
    const selectedServingLabel = selectedServingSize?.label
      ? formatServingSizeLabel(selectedServingSize.label)
      : selectedServingId
        ? formatServingSizeLabel(selectedServingId)
        : '';
    const selectedServingGrams = toPositiveNumber(record.selectedServingGrams);
    const servingGrams = toPositiveNumber(record.servingGrams);
    const gramsPerServing = selectedServingGrams
      ?? servingGrams
      ?? (selectedServingSize?.grams && selectedServingSize.grams > 0 ? selectedServingSize.grams : null);
    const totalGrams = gramsPerServing != null
      ? gramsPerServing * servings
      : null;
    const gramsWithUnit = [
      totalGrams != null ? `${formatDisplayQuantity(totalGrams)} grms` : '',
      selectedServingLabel,
    ].filter((segment) => segment.length > 0).join(' ');

    return [
      `${formatDisplayQuantity(servings)} Serving${servings > 1 ? 's' : ''}`,
      gramsWithUnit,
    ].filter((segment) => segment.length > 0);
  }

  const amount = toPositiveNumber(record.amount) ?? toPositiveNumber(recipe.nutritionServings ?? recipe.servings) ?? 1;
  const rawUnit = typeof record.servingId === 'string' && record.servingId.trim().length > 0
    ? record.servingId
    : 'serving';
  const amountGrams = toPositiveNumber(record.amountGrams);

  return [
    `${formatDisplayQuantity(amount)} ${formatDisplayUnit(rawUnit)}`.trim(),
    amountGrams != null ? `${formatDisplayQuantity(amountGrams)} gm` : '',
  ].filter((segment) => segment.length > 0);
}

function buildRowsForMeal(entry: MealEntry): RecipeDisplayRow[] {
  if (!entry.recipes.length) return [];

  return entry.recipes.slice(0, 4).map((recipe) => {
    const calories = getRecipeCalories(recipe);
    const itemType = String((recipe as { itemType?: unknown }).itemType ?? '').trim().toLowerCase();
    const recipeId =
      itemType === 'recipe'
        ? String(recipe.recipeId ?? (recipe as { id?: unknown }).id ?? '').trim()
        : String(recipe.recipeId ?? '').trim();
    const isRecipe = itemType ? itemType === 'recipe' : recipeId.length > 0;
    const detailLabel = buildRowDetailSegments(recipe).join(' · ');

    return {
      image: recipe.recipeImage || DEFAULT_RECIPE_IMAGE,
      title: recipe.recipeName || (recipe as { name?: string }).name || 'Recipe',
      detailLabel,
      caloriesLabel: `${calories} kcal`,
      recipeId,
      itemId: recipe.itemId,
      notes: typeof recipe.customNotes === 'string'
        ? recipe.customNotes
        : recipe.customNotes === null
          ? null
          : typeof (recipe as { notes?: unknown }).notes === 'string'
            ? String((recipe as { notes?: string }).notes)
            : undefined,
      recipeSource: recipe.recipeSource,
      isRecipe,
      defaultServingId: recipe.defaultServingId ?? null,
      defaultServingGrams: recipe.defaultServingGrams ?? null,
      selectedServingId: recipe.selectedServingId ?? null,
      selectedServingGrams: recipe.selectedServingGrams ?? null,
      servingSizes: Array.isArray(recipe.servingSizes) ? recipe.servingSizes : undefined,
      optionGroupId: typeof (recipe as { optionGroupId?: unknown }).optionGroupId === 'string'
        ? (recipe as { optionGroupId?: string }).optionGroupId || null
        : null,
      optionGroupLabel: typeof (recipe as { optionGroupLabel?: unknown }).optionGroupLabel === 'string'
        ? (recipe as { optionGroupLabel?: string }).optionGroupLabel || null
        : null,
    };
  });
}

type GenericOptionGroup = {
  key: string;
  optionGroupId: string | null;
  optionGroupLabel: string;
  rows: RecipeDisplayRow[];
};

function getGenericOptionItemId(row: RecipeDisplayRow): string {
  return (row.itemId || row.recipeId || '').trim();
}

function buildGenericOptionGroups(rows: RecipeDisplayRow[]): GenericOptionGroup[] {
  const groups = new Map<string, GenericOptionGroup>();
  rows.forEach((row, index) => {
    const itemId = getGenericOptionItemId(row);
    const optionGroupId = row.optionGroupId?.trim() || null;
    const key = optionGroupId || `single:${itemId || index}`;
    const existing = groups.get(key);
    if (existing) {
      existing.rows.push(row);
      return;
    }
    groups.set(key, {
      key,
      optionGroupId,
      optionGroupLabel: row.optionGroupLabel?.trim() || (optionGroupId ? `Option ${groups.size + 1}` : row.title),
      rows: [row],
    });
  });
  return Array.from(groups.values());
}

function clampAnalysisPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function buildDayAnalysis(day: FinalizedMealDay, targetDaily?: FinalizedMealPlanData['targetDaily']): DayAnalysisSnapshot {
  const mealEntries = getMealEntries(day);

  const totalByMeals = mealEntries.reduce(
    (acc, entry) => {
      const mealData = day.meals?.[entry.key];
      acc.calories += getMealSlotCalories(mealData, entry.recipes);
      acc.protein += getMealSlotMacro(mealData, entry.recipes, 'protein');
      acc.carbs += getMealSlotMacro(mealData, entry.recipes, 'carbs');
      acc.fat += getMealSlotMacro(mealData, entry.recipes, 'fat');
      acc.fiber += getMealSlotMacro(mealData, entry.recipes, 'fiber');
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
  );

  const dayMacros = day.dayTotals?.macros;
  const caloriesTarget = Math.max(1, Number(targetDaily?.calories ?? 2000) || 2000);
  const proteinTarget = Math.max(1, resolveRangeMidpoint(targetDaily?.proteinMin, targetDaily?.proteinMax, 75) ?? 75);
  const carbsTarget = Math.max(1, resolveRangeMidpoint(targetDaily?.carbsMin, targetDaily?.carbsMax, 230) ?? 230);
  const fatTarget = Math.max(1, resolveRangeMidpoint(targetDaily?.fatMin, targetDaily?.fatMax, 85) ?? 85);
  const fiberTarget = 35;

  const totalCaloriesRaw = (typeof dayMacros?.calories === 'number' ? dayMacros.calories : totalByMeals.calories) || 0;
  const totalProteinRaw = (typeof dayMacros?.protein === 'number' ? dayMacros.protein : totalByMeals.protein) || 0;
  const totalCarbsRaw = (typeof dayMacros?.carbs === 'number' ? dayMacros.carbs : totalByMeals.carbs) || 0;
  const totalFatRaw = (typeof dayMacros?.fat === 'number' ? dayMacros.fat : totalByMeals.fat) || 0;
  const totalFiberRaw = (typeof dayMacros?.fiber === 'number' ? dayMacros.fiber : totalByMeals.fiber) || 0;

  const totalCalories = Math.max(0, totalCaloriesRaw);
  const totalProtein = Math.max(0, totalProteinRaw);
  const totalCarbs = Math.max(0, totalCarbsRaw);
  const totalFat = Math.max(0, totalFatRaw);
  const totalFiber = Math.max(0, totalFiberRaw);

  const macroCalorieSum = Math.max(1, totalProtein * 4 + totalCarbs * 4 + totalFat * 9);
  const macroSplit = {
    carbs: clampAnalysisPercent((totalCarbs * 4 * 100) / macroCalorieSum),
    protein: clampAnalysisPercent((totalProtein * 4 * 100) / macroCalorieSum),
    fat: clampAnalysisPercent((totalFat * 9 * 100) / macroCalorieSum),
  };

  const calcium = day.dayTotals?.micronutrients?.minerals?.calcium?.quantity || 0;
  const iron = day.dayTotals?.micronutrients?.minerals?.iron?.quantity || 0;
  const vitaminD = day.dayTotals?.micronutrients?.vitamins?.vitaminD?.quantity || 0;

  const pctEnergy = nutrientVsGoalPercent(totalCalories, caloriesTarget);
  const pctProtein = nutrientVsGoalPercent(totalProtein, proteinTarget);
  const pctCarbs = nutrientVsGoalPercent(totalCarbs, carbsTarget);
  const pctFat = nutrientVsGoalPercent(totalFat, fatTarget);
  const pctFiber = nutrientVsGoalPercent(totalFiber, fiberTarget);
  const pctCalcium = nutrientVsGoalPercent(calcium, 1000);
  const pctIron = nutrientVsGoalPercent(iron, 18);
  const pctVitD = nutrientVsGoalPercent(vitaminD, 15);

  const nutrientVsRda: NutrientVsRdaBar[] = [
    { key: 'energy', label: 'Energy', percent: pctEnergy, color: getTrafficLightColor(pctEnergy, false) },
    { key: 'protein', label: 'Protein', percent: pctProtein, color: getTrafficLightColor(pctProtein, false) },
    { key: 'carbs', label: 'Carbs', percent: pctCarbs, color: getTrafficLightColor(pctCarbs, false) },
    { key: 'fat', label: 'Fat', percent: pctFat, color: getTrafficLightColor(pctFat, false) },
    { key: 'fibre', label: 'Fibre', percent: pctFiber, color: getTrafficLightColor(pctFiber, true) },
    { key: 'calcium', label: 'Calcium', percent: pctCalcium, color: getMicronutrientTrafficLightColor(calcium, 1000) },
    { key: 'iron', label: 'Iron', percent: pctIron, color: getMicronutrientTrafficLightColor(iron, 18) },
    { key: 'vitd', label: 'Vit D', percent: pctVitD, color: getMicronutrientTrafficLightColor(vitaminD, 15) },
  ];

  const mealCalories = mealEntries
    .map((entry, index) => {
      const mealData = day.meals?.[entry.key];
      const calories = Math.max(0, getMealSlotCalories(mealData, entry.recipes));
      const percent = totalCalories > 0 ? clampAnalysisPercent((calories * 100) / totalCalories) : 0;
      return {
        key: entry.key,
        label: toAnalysisMealLabel(entry.key),
        calories,
        percent,
        color: getMealAccent(entry.key, index),
      };
    })
    .filter((row) => row.calories > 0);

  const dayMacroRecord =
    dayMacros && typeof dayMacros === 'object'
      ? (dayMacros as Record<string, unknown>)
      : undefined;
  const fatBreakdownFromMeals = getDayFatBreakdownTotalsFromMeals(day, mealEntries);
  const resolveFatBreakdownValue = (key: FatBreakdownMetricKey): number | null => {
    const fromDayTotals = getNumericValue(dayMacroRecord, fatBreakdownAliasesByKey[key]);
    if (fromDayTotals !== null) return Math.max(0, Number(fromDayTotals.toFixed(1)));
    return fatBreakdownFromMeals[key];
  };

  const fatBreakdownValues = [
    { key: 'mono', label: 'Monounsaturated', value: resolveFatBreakdownValue('mufa'), color: '#2fb0d4' },
    { key: 'poly', label: 'Polyunsaturated', value: resolveFatBreakdownValue('pufa'), color: '#d5dbe3' },
    { key: 'trans', label: 'Trans', value: resolveFatBreakdownValue('transFat'), color: '#8dd9f2' },
    { key: 'saturated', label: 'Saturated', value: resolveFatBreakdownValue('saturatedFat'), color: '#2cb3b8' },
  ].filter((item): item is { key: string; label: string; value: number; color: string } => item.value != null);

  const fatBreakdownTotal = fatBreakdownValues.reduce((sum, item) => sum + item.value, 0);
  const fatBreakdown: FatBreakdownRow[] = fatBreakdownValues.map((item) => ({
    ...item,
    percent: fatBreakdownTotal > 0 ? clampAnalysisPercent((item.value * 100) / fatBreakdownTotal) : 0,
  }));

  const topContributors = mealEntries
    .flatMap((entry, mealIndex) =>
      entry.recipes.map((recipe, recipeIndex) => {
        const calories = getRecipeCalories(recipe);
        return {
          key: `${entry.key}-${recipe.recipeId || recipeIndex}`,
          title: recipe.recipeName || 'Recipe',
          detailLabel: buildRowDetailSegments(recipe).join(' · '),
          mealLabel: toAnalysisMealLabel(entry.key),
          mealColor: getMealAccent(entry.key, mealIndex),
          calories,
          percent: totalCalories > 0 ? clampAnalysisPercent((calories * 100) / totalCalories) : 0,
          color: getMealAccent(entry.key, mealIndex),
        };
      })
    )
    .sort((a, b) => b.calories - a.calories)
    .slice(0, 10)
    .map((item) => ({
      key: item.key,
      title: item.title,
      detailLabel: item.detailLabel,
      mealLabel: item.mealLabel,
      mealColor: item.mealColor,
      calories: item.calories,
      percent: item.percent,
      color: item.color,
    }));

  return {
    dayLabel: day.dayName?.trim() || `Day ${day.day}`,
    totalCalories,
    totalMeals: mealEntries.length,
    macroSplit,
    nutrientVsRda,
    mealCalories,
    macroProgressCards: [
      { key: 'protein', label: 'Protein', value: totalProtein, target: proteinTarget, color: getTrafficLightColor(nutrientVsGoalPercent(totalProtein, proteinTarget), false) },
      { key: 'carbs', label: 'Carbohydrates', value: totalCarbs, target: carbsTarget, color: getTrafficLightColor(nutrientVsGoalPercent(totalCarbs, carbsTarget), false) },
      { key: 'fiber', label: 'Dietary Fibre', value: totalFiber, target: fiberTarget, color: getTrafficLightColor(nutrientVsGoalPercent(totalFiber, fiberTarget), true) },
    ],
    totalFat,
    fatTarget,
    fatBreakdown,
    topContributors,
  };
}

/**
 * Build the analysis snapshot using the same data-loading + snapshot pipeline as
 * `NutritionalAnalysisPage`, so both entry points render identical numbers.
 */
function buildLoadedDayAnalysisSnapshot(
  loaded: NutritionalAnalysisLoadedData,
  drvContext?: ManualNutritionDrvContext | null,
): DayAnalysisSnapshot {
  const mealCards = [...loaded.mealMeta]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((meal) => {
      const rowTotals = rowMacroTotals(loaded.rowsByMeal[meal.name] ?? []);
      const apiMealTotals = loaded.apiMealMacroTotalsByMealName[meal.name];
      return {
        name: meal.name,
        totals: apiMealTotals !== undefined ? apiMealTotals : rowTotals,
      };
    });

  const totalIntake = loaded.apiDayMacroTotals ?? mealCards.reduce(
    (acc, meal) => ({
      calories: acc.calories + Math.max(0, meal.totals.calories),
      protein: acc.protein + Math.max(0, meal.totals.protein),
      carbs: acc.carbs + Math.max(0, meal.totals.carbs),
      fat: acc.fat + Math.max(0, meal.totals.fat),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 },
  );

  const namedDayLabel = loaded.dayNamesByNumber[loaded.effectiveDayNumber]?.trim();
  const dayLabel = namedDayLabel && namedDayLabel.length > 0
    ? namedDayLabel
    : `Day ${loaded.effectiveDayNumber}`;

  return buildManualNutritionAnalysisSnapshot(
    dayLabel,
    mealCards,
    totalIntake,
    loaded.macroTargets,
    loaded.rowsByMeal,
    drvContext ?? null,
    loaded.apiTopCalorieContributors,
    loaded.apiNutrientTotals,
    loaded.apiFatBreakdownTotals,
  );
}

function renderTimelineNode(children: React.ReactNode, lineHeight = 58) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr)', columnGap: 0.65 }}>
      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ mt: 0.95, width: 6, height: 6, borderRadius: '50%', bgcolor: '#25a8da' }} />
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            height: lineHeight,
            width: '1px',
            bgcolor: '#dbe6ee',
          }}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function normalizeDayTitle(value: string, dayNumber: number): string {
  const trimmed = value.trim();
  return trimmed || `Day ${dayNumber}`;
}

interface DeleteDayDialogProps {
  open: boolean;
  isSubmitting: boolean;
  dayLabel: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

function DeleteDayDialog({
  open,
  isSubmitting,
  dayLabel,
  onClose,
  onConfirm,
}: DeleteDayDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          px: { xs: 1.75, sm: 2.5 },
          py: { xs: 2, sm: 2.5 },
        },
      }}
    >
      <Box sx={{ display: 'grid', justifyItems: 'center', textAlign: 'center' }}>
        <Box
          sx={{
            width: { xs: 66, sm: 72 },
            height: { xs: 66, sm: 72 },
            borderRadius: '14px',
            bgcolor: '#fff1f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box component="img" src={trashIcon} alt="" sx={{ width: { xs: 34, sm: 38 }, height: { xs: 34, sm: 38 }, display: 'block' }} />
        </Box>

        <Typography
          sx={{
            mt: 1.75,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: { xs: '22px', sm: '24px' },
            lineHeight: { xs: '30px', sm: '32px' },
            color: '#0b1116',
            letterSpacing: '-0.03em',
          }}
        >
          Remove day?
        </Typography>

        <Typography
          sx={{
            mt: 0.75,
            maxWidth: 420,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: { xs: '12px', sm: '13px' },
            lineHeight: { xs: '18px', sm: '20px' },
            color: '#667085',
            letterSpacing: '-0.02em',
          }}
        >
          Are you sure you want to remove {dayLabel} from this meal plan?
        </Typography>

        <Box
          sx={{
            mt: 2.25,
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              height: 44,
              borderRadius: '16px',
              bgcolor: '#25a8da',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '13px' },
              lineHeight: 1.2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="outlined"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              height: 44,
              borderRadius: '16px',
              borderColor: '#d0d5dd',
              color: '#344054',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '13px' },
              lineHeight: 1.2,
              textTransform: 'none',
              '&:hover': { borderColor: '#98a2b3', bgcolor: '#f8fafc' },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <CircularProgress size={18} thickness={6} sx={{ color: '#344054' }} />
                Removing...
              </Box>
            ) : (
              'Yes, remove'
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

const DayCardOverlay = ({ day, targetDaily, isDayExpanded }: { day: FinalizedMealDay, targetDaily: any, isDayExpanded: (mealKey: string) => boolean }) => {
  const macros = day.dayTotals?.macros;
  const calories = Math.max(0, Number(macros?.calories) || 0);
  const protein = Math.max(0, Number(macros?.protein) || 0);
  const carbs = Math.max(0, Number(macros?.carbs) || 0);
  const fat = Math.max(0, Number(macros?.fat) || 0);
  const caloriesTarget = Math.max(1, Number(targetDaily.calories ?? 2000) || 2000);
  const proteinTarget = Math.max(1, resolveRangeMidpoint(targetDaily.proteinMin, targetDaily.proteinMax, 160) ?? 160);
  const carbsTarget = Math.max(1, resolveRangeMidpoint(targetDaily.carbsMin, targetDaily.carbsMax, 160) ?? 160);
  const fatTarget = Math.max(1, resolveRangeMidpoint(targetDaily.fatMin, targetDaily.fatMax, 85) ?? 85);

  const macroCards = [
    { key: 'calories', label: 'Calories', value: calories, unit: 'kcal' as const, target: caloriesTarget, progress: getProgressValue(calories, caloriesTarget) },
    { key: 'carbs', label: 'Carbs', value: carbs, unit: 'g' as const, target: carbsTarget, progress: getProgressValue(carbs, carbsTarget) },
    { key: 'protein', label: 'Protein', value: protein, unit: 'g' as const, target: proteinTarget, progress: getProgressValue(protein, proteinTarget) },
    { key: 'fat', label: 'Fat', value: fat, unit: 'g' as const, target: fatTarget, progress: getProgressValue(fat, fatTarget) },
  ];

  const mealEntries = getMealEntries(day);

  return (
    <Paper
      elevation={8}
      sx={{
        width: 340,
        borderRadius: '16px',
        bgcolor: '#fff',
        opacity: 0.95,
        border: '2px solid #25a8da',
        boxShadow: '0 20px 40px -10px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 1.5, py: 1.35, bgcolor: '#35add2', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
        <DragIndicatorRoundedIcon sx={{ fontSize: 18, opacity: 0.95 }} />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', fontWeight: 600 }}>
          {day.dayName?.trim() || `Day ${day.day}`}
        </Typography>
      </Box>
      <Box sx={{ p: 1.2, pb: 0, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.65 }}>
        {macroCards.map((card, i) => {
          const trend = getMacroTrendFromSuggestedRda(card.value, card.target);
          const TrendIcon = trend.Icon;
          return (
          <Box
            key={i}
            sx={{
              px: 0.65,
              py: 0.75,
              minHeight: 72,
              borderRadius: '10px',
              bgcolor: '#f4f8fb',
              border: '1px solid #e2e8ed',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.25, mb: 0.15 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#475467', textTransform: 'uppercase', textAlign: 'left', minWidth: 0 }}>
                {card.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1, flexShrink: 0 }}>
                <TrendIcon sx={{ fontSize: 11, color: trend.color }} />
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: trend.color, fontVariantNumeric: 'tabular-nums' }}>
                  {trend.label}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 0.35, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                {roundNutritionDisplay(card.value)}{' '}
                <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>
                  {card.unit === 'kcal' ? 'kcal' : 'g'}
                </Box>
              </Typography>
              <Typography sx={{ mt: 0.2, fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 500, color: '#667085', fontVariantNumeric: 'tabular-nums' }}>
                of {roundNutritionDisplay(card.target)} {card.unit === 'kcal' ? 'kcal' : 'g'}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={card.progress} sx={{ mt: 0.45, height: 4, borderRadius: 99, bgcolor: '#EAECF0', '& .MuiLinearProgress-bar': { bgcolor: trend.barColor }, width: '72%', mx: 'auto' }} />
          </Box>
          );
        })}
      </Box>
      <Box sx={{ px: 1.2, pb: 1.25, mt: 0.65 }}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '18px', letterSpacing: '0.6px', color: '#111827', fontWeight: 600, textTransform: 'uppercase', mb: 1.35 }}>
          MEAL SCHEDULE
        </Typography>
        <Stack spacing={0.9}>
          {mealEntries.map((entry, i) => {
            const isExpanded = isDayExpanded(entry.key);

            if (isExpanded) {
              const rows = buildRowsForMeal(entry);
              return (
                <Box key={`expanded-${i}`}>
                  <Box
                    sx={{
                      borderRadius: '9px',
                      bgcolor: '#26a8d6',
                      color: '#fff',
                      px: 0.9,
                      py: 0.72,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.8,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                      <DragIndicatorRoundedIcon sx={{ fontSize: 16, opacity: 0.95 }} />
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.98rem', fontWeight: 500, color: '#fff', whiteSpace: 'nowrap' }}>
                        {entry.label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65 }}>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 500, color: '#fff', whiteSpace: 'nowrap' }}>
                        {entry.timeLabel}
                      </Typography>
                      <KeyboardArrowUpRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                    </Box>
                  </Box>
                  <Box sx={{ mt: 0.65, borderRadius: '8px', border: '1px solid #d9e1e8', bgcolor: '#fff', pl: 1.45, pr: 1.1, pt: 1.35, pb: 1.2, minHeight: rows.length ? 'auto' : 76 }}>
                    {rows.length ? (
                      <Stack spacing={1.35}>
                        {rows.map((row, index) => (
                          <Box key={`${entry.key}-row-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                            <Avatar src={row.image} alt={row.title} variant="rounded" sx={{ width: 34, height: 34, borderRadius: '8px', flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#344054', lineHeight: 1.25 }}>{row.title}</Typography>
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#99a5b4', mt: 0.08 }}>
                                {row.detailLabel ? `${row.detailLabel} · ` : null}
                                <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                                  {row.caloriesLabel}
                                </Box>
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#98a2b3' }}>No recipes added in this meal.</Typography>
                    )}
                  </Box>
                </Box>
              );
            }

            return (
              <Box key={`collapsed-${i}`} sx={{ borderRadius: '9px', border: '1px solid #dbe3ea', bgcolor: '#fff', px: 0.85, py: 0.78, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                  <DragIndicatorRoundedIcon sx={{ fontSize: 16, opacity: 0.6, mr: 0.3 }} />
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', fontWeight: 500, color: '#111827', whiteSpace: 'nowrap' }}>
                    {entry.label}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 500, color: '#344054', whiteSpace: 'nowrap' }}>
                    {entry.timeLabel}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
};

export const ClientDetailsV2MealPlanBoardView: React.FC<ClientDetailsV2MealPlanBoardViewProps> = ({
  planName,
  planDurationLabel,
  data,
  client,
  onBack,
  onEditDay,
  onAddDay,
  onCopyDay,
  isCopyingDay = false,
  onSaveDayName,
  isSavingDayName,
  onDeleteDay,
  isDeletingDay,
  onReorderDays,
  isReorderingDays,
  onReorderMealsInDay,
  isReorderingMeals,
  onMealPlanUpdated,
  hidePlanTabs = false,
  readOnly = false,
  editDayLabel,
  hideAddDayUi = false,
  fillViewport = false,
  planMode = 'manual',
  planCategory,
  hideGenericToolbarAddMeal = false,
  onExportPdf,
  isExportingPdf = false,
  onFinalize,
  isFinalizing = false,
  onSaveMealPlanNotes,
  isSavingMealPlanNotes = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDraftPlan = data.status === 'draft';
  const canShowExportPdf = Boolean(onExportPdf) && !isDraftPlan;
  const canEditNotes = Boolean(onSaveMealPlanNotes) && !readOnly;
  const planPractitionerNotes = React.useMemo(() => {
    const planNotes = typeof data.planNotes === 'string' ? data.planNotes.trim() : '';
    const description = typeof data.description === 'string' ? data.description.trim() : '';
    if (isDraftPlan) return planNotes || description;
    return description || planNotes;
  }, [data.description, data.planNotes, isDraftPlan]);
  const targetDaily = React.useMemo(() => {
    const cg = data.searchParams?.clientGoals;
    const cli = resolveClientProfileMacroTargets(client);
    const eerKcal = resolveClientEerDailyKcal(client);
    const td = data.targetDaily;
    const targetValue = (...values: unknown[]) => {
      for (const value of values) {
        const parsed = readPositiveMacroNumber(value);
        if (parsed !== undefined) return parsed;
      }
      return undefined;
    };

    if (mealPlanTargetDailyHasNumericGoals(td) && td) {
      return {
        calories: targetValue(td.calories, cg?.calories, eerKcal),
        proteinMin: targetValue(td.proteinMin, cli.proteinExact, cg?.protein, cg?.proteinMin, cli.proteinMin),
        proteinMax: targetValue(td.proteinMax, cli.proteinExact, cg?.protein, cg?.proteinMax, cli.proteinMax),
        carbsMin: targetValue(td.carbsMin, cli.carbsExact, cg?.carbs, cg?.carbsMin, cli.carbsMin),
        carbsMax: targetValue(td.carbsMax, cli.carbsExact, cg?.carbs, cg?.carbsMax, cli.carbsMax),
        fatMin: targetValue(td.fatMin, cli.fatExact, cg?.fat, cg?.fatMin, cli.fatMin),
        fatMax: targetValue(td.fatMax, cli.fatExact, cg?.fat, cg?.fatMax, cli.fatMax),
      };
    }
    return {
      calories: targetValue(cg?.calories, eerKcal),
      proteinMin: targetValue(cli.proteinExact, cg?.protein, cg?.proteinMin, cli.proteinMin),
      proteinMax: targetValue(cli.proteinExact, cg?.protein, cg?.proteinMax, cli.proteinMax),
      carbsMin: targetValue(cli.carbsExact, cg?.carbs, cg?.carbsMin, cli.carbsMin),
      carbsMax: targetValue(cli.carbsExact, cg?.carbs, cg?.carbsMax, cli.carbsMax),
      fatMin: targetValue(cli.fatExact, cg?.fat, cg?.fatMin, cli.fatMin),
      fatMax: targetValue(cli.fatExact, cg?.fat, cg?.fatMax, cli.fatMax),
    };
  }, [client, data.searchParams?.clientGoals, data.targetDaily]);
  const copyFromOptions = React.useMemo(() => {
    const formatMacro = (value: number): string => `${Math.max(0, Number(value.toFixed(1)))}g`;

    return data.days
      .slice()
      .sort((a, b) => a.day - b.day)
      .map((day) => {
        const name = day.dayName?.trim() || `Day ${day.day}`;
        const macroTotals = day.dayTotals?.macros;
        const calories = Math.max(0, Number(macroTotals?.calories) || 0);
        const protein = typeof macroTotals?.protein === 'number' ? macroTotals.protein : 0;
        const carbs = typeof macroTotals?.carbs === 'number' ? macroTotals.carbs : 0;
        const fat = typeof macroTotals?.fat === 'number' ? macroTotals.fat : 0;
        const mealCount = getMealEntries(day).filter((entry) => entry.recipes.length > 0).length;
        const detail = `${roundNutritionDisplay(calories)} kcal · ${mealCount} meals · P:${formatMacro(protein)} C:${formatMacro(carbs)} F:${formatMacro(fat)}`;
        return { id: String(day.day), name, detail };
      });
  }, [data.days]);

  const [copyTargetDay, setCopyTargetDay] = React.useState<number | null>(null);
  const copyTargetDayMeta = React.useMemo(() => {
    if (copyTargetDay == null) return undefined;
    return data.days.find((d) => d.day === copyTargetDay);
  }, [copyTargetDay, data.days]);
  const copyTargetDayLabel = copyTargetDayMeta?.dayName?.trim() || (copyTargetDay != null ? `Day ${copyTargetDay}` : '');
  const copyFromOptionsForTarget = React.useMemo(() => {
    if (copyTargetDay == null) return [];
    return copyFromOptions.filter((opt) => Number(opt.id) !== copyTargetDay);
  }, [copyFromOptions, copyTargetDay]);

  const handleOpenCopyDayDialog = React.useCallback((dayNumber: number) => {
    setCopyTargetDay(dayNumber);
  }, []);

  const handleCloseCopyDayDialog = React.useCallback(() => {
    if (isCopyingDay) return;
    setCopyTargetDay(null);
  }, [isCopyingDay]);

  const handleSubmitCopyDay = React.useCallback(
    async (payload: { sourceDay: number; targetDays: number[] }) => {
      if (copyTargetDay == null || !onCopyDay) return;
      trackMealPlanDayCopyClicked({
        product_area: 'meal_plans',
        meal_plan_surface: 'board_view',
      });
      try {
        await onCopyDay({ sourceDay: payload.sourceDay, targetDays: payload.targetDays });
        setCopyTargetDay(null);
      } catch {
        // Tab shows toast
      }
    },
    [copyTargetDay, onCopyDay]
  );

  const [activePlanTab, setActivePlanTab] = React.useState<'generic' | 'day'>(() => (planMode === 'generic' ? 'generic' : 'day'));
  const [updatingGenericOptionItemId, setUpdatingGenericOptionItemId] = React.useState<string | null>(null);
  const handleUpdateGenericOptionGroup = React.useCallback(
    async ({
      entry,
      row,
      optionGroupId,
      optionGroupLabel,
      selectedOptionGroupId,
    }: {
      entry: MealEntry;
      row: RecipeDisplayRow;
      optionGroupId?: string | null;
      optionGroupLabel?: string | null;
      selectedOptionGroupId?: string | null;
    }) => {
      if (planMode !== 'generic' || readOnly) return;
      const itemId = getGenericOptionItemId(row);
      if (!itemId) {
        showErrorToast(new Error('Unable to identify this item'), 'Unable to update option');
        return;
      }

      setUpdatingGenericOptionItemId(itemId);
      try {
        const response = await updateGenericMealPlanItem({
          genericPlanId: data.id,
          mealName: entry.label,
          itemId,
          optionGroupId,
          optionGroupLabel,
          selectedOptionGroupId,
        });
        if (!response.success) {
          throw new Error(response.message || 'Unable to update option');
        }
        await onMealPlanUpdated?.();
        showSuccessToast('Cohort meal option updated');
      } catch (error) {
        showErrorToast(error, 'Unable to update option');
      } finally {
        setUpdatingGenericOptionItemId(null);
      }
    },
    [data.id, onMealPlanUpdated, planMode, readOnly]
  );
  const handleCreateGenericAndOption = React.useCallback(
    async (entry: MealEntry, rows: RecipeDisplayRow[], optionGroupLabel: string) => {
      if (planMode !== 'generic' || readOnly) return;
      const candidates = rows.filter((row) => !row.optionGroupId && getGenericOptionItemId(row));
      if (candidates.length < 2) {
        showWarningToast('Add at least two ungrouped foods to create an AND option.');
        return;
      }

      const optionGroupId = `and-${Date.now()}`;
      setUpdatingGenericOptionItemId(optionGroupId);
      try {
        const responses = await Promise.all(
          candidates.slice(0, 2).map((row) => updateGenericMealPlanItem({
            genericPlanId: data.id,
            mealName: entry.label,
            itemId: getGenericOptionItemId(row),
            optionGroupId,
            optionGroupLabel,
            selectedOptionGroupId: optionGroupId,
          }))
        );
        const failedResponse = responses.find((response) => !response.success);
        if (failedResponse) {
          throw new Error(failedResponse.message || 'Unable to create AND option');
        }
        await onMealPlanUpdated?.();
        showSuccessToast('AND option created');
      } catch (error) {
        showErrorToast(error, 'Unable to create AND option');
      } finally {
        setUpdatingGenericOptionItemId(null);
      }
    },
    [data.id, onMealPlanUpdated, planMode, readOnly]
  );
  const [analysisDayNumber, setAnalysisDayNumber] = React.useState<number | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] = React.useState<DayAnalysisSnapshot | null>(null);
  const [isAnalysisLoading, setIsAnalysisLoading] = React.useState(false);
  const [analysisDrvContext, setAnalysisDrvContext] = React.useState<ManualNutritionDrvContext | null>(null);
  const handleViewDetailedAnalysisReport = React.useCallback(() => {
    const clientId = String(client?.id ?? '').trim();
    const mealPlanId = String(data.id ?? '').trim();
    if (!clientId) return;

    trackMealPlanDetailedReportClicked({
      product_area: 'meal_plans',
      meal_plan_surface: 'board_view',
    });

    const query = new URLSearchParams();
    if (mealPlanId) query.set('mealPlanId', mealPlanId);
    if (analysisDayNumber != null && analysisDayNumber > 0) query.set('day', String(analysisDayNumber));

    const backQuery = new URLSearchParams(location.search);
    if (planCategory) backQuery.set('category', planCategory);
    if (mealPlanId) backQuery.set('mealPlanId', mealPlanId);
    if (analysisDayNumber != null && analysisDayNumber > 0) backQuery.set('day', String(analysisDayNumber));
    const backSearch = backQuery.toString();
    const analysisBackPath = `${location.pathname}${backSearch ? `?${backSearch}` : ''}`;

    setAnalysisDayNumber(null);
    navigate(`/clients/${encodeURIComponent(clientId)}/details-v2/analysis${query.toString() ? `?${query.toString()}` : ''}`, {
      state: { analysisBackPath },
    });
  }, [analysisDayNumber, client?.id, data.id, location.pathname, location.search, navigate, planCategory]);
  const [isAddDayDialogOpen, setIsAddDayDialogOpen] = React.useState(false);
  const [genericTemplateSearch, setGenericTemplateSearch] = React.useState('');
  const [genericTemplateOptions, setGenericTemplateOptions] = React.useState<GenericTemplateOption[]>([]);
  const [isGenericTemplateLoading, setIsGenericTemplateLoading] = React.useState(false);
  const [genericTemplateError, setGenericTemplateError] = React.useState('');
  const [importingGenericTemplateId, setImportingGenericTemplateId] = React.useState<string | null>(null);
  const [templatePickerSearch, setTemplatePickerSearch] = React.useState('');
  const [templatePickerAnchorEl, setTemplatePickerAnchorEl] = React.useState<HTMLElement | null>(null);
  const [templatePickerPage, setTemplatePickerPage] = React.useState(1);
  const [templatePickerTotalPages, setTemplatePickerTotalPages] = React.useState(1);
  const [templatePickerTotalCount, setTemplatePickerTotalCount] = React.useState(0);
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = React.useState(false);
  const [isGenericSectionVisible, setIsGenericSectionVisible] = React.useState(false);
  const [expandedMealRows, setExpandedMealRows] = React.useState<Record<string, boolean>>({});
  const [recipeViewSelection, setRecipeViewSelection] = React.useState<{
    recipeId: string;
    recipeName: string;
    mealName: string;
    day: number;
    mealOrder?: number;
    source: 'spoonacular' | 'edamam' | 'internal';
    itemId?: string;
    defaultServingId?: string | null;
    defaultServingGrams?: number | null;
    selectedServingId?: string | null;
    selectedServingGrams?: number | null;
    servingSizes?: RecipeServingSize[];
  } | null>(null);
  const [unsavedDayNames, setUnsavedDayNames] = React.useState<Record<number, string>>({});
  const [editingDayNumber, setEditingDayNumber] = React.useState<number | null>(null);
  const [pendingDeleteDayNumber, setPendingDeleteDayNumber] = React.useState<number | null>(null);
  const [draggingDayNumber, setDraggingDayNumber] = React.useState<number | null>(null);
  const [dragOverDayNumber, setDragOverDayNumber] = React.useState<number | null>(null);
  const [draggingMealKey, setDraggingMealKey] = React.useState<string | null>(null);
  const [draggingMealDayNumber, setDraggingMealDayNumber] = React.useState<number | null>(null);
  const [dragOverMealKey, setDragOverMealKey] = React.useState<string | null>(null);
  const [dragOverMealDayNumber, setDragOverMealDayNumber] = React.useState<number | null>(null);

  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [activeType, setActiveType] = React.useState<'Day' | 'Meal' | null>(null);

  const dayHeaderRefs = React.useRef<Record<number, HTMLDivElement | null>>({});
  const dayByNumber = React.useMemo(() => {
    return new Map(data.days.map((day) => [day.day, day]));
  }, [data.days]);

  const maxDayNumber = React.useMemo(
    () => data.days.reduce((maxValue, day) => Math.max(maxValue, day.day || 0), 0),
    [data.days]
  );
  const nextDayNumber = Math.max(1, maxDayNumber + 1);
  const defaultDayName = `Day ${nextDayNumber}`;

  const handleOpenAddDayDialog = React.useCallback(() => {
    setIsAddDayDialogOpen(true);
  }, []);

  const handleViewBoardRecipe = React.useCallback((dayNumber: number, mealKey: string, row: RecipeDisplayRow, mealOrder?: number) => {
    if (!row.isRecipe) return;
    const recipeIdCandidate = String(row.recipeId ?? '').trim();
    if (!recipeIdCandidate) {
      showWarningToast('Recipe unavailable', 'Recipe details are unavailable for this item.');
      return;
    }
    const itemIdTrim = String(row.itemId ?? '').trim() || undefined;
    setRecipeViewSelection({
      recipeId: recipeIdCandidate,
      recipeName: row.title,
      mealName: mealKey,
      day: dayNumber,
      mealOrder,
      source: (row.recipeSource ?? 'spoonacular') as 'spoonacular' | 'edamam' | 'internal',
      itemId: itemIdTrim,
      defaultServingId: row.defaultServingId ?? null,
      defaultServingGrams: row.defaultServingGrams ?? null,
      selectedServingId: row.selectedServingId ?? null,
      selectedServingGrams: row.selectedServingGrams ?? null,
      servingSizes: row.servingSizes,
    });
  }, []);

  const handleSubmitAddDay = React.useCallback(
    (payload: AddDayPayload) => {
      onAddDay?.(payload);
    },
    [onAddDay]
  );

  React.useEffect(() => {
    let cancelled = false;

    if (!client) {
      setAnalysisDrvContext(null);
      return () => {
        cancelled = true;
      };
    }

    const clientRecord = client as ClientWithProfileTargets;
    const drvRequirements = clientRecord.drvRequirements ?? null;
    const source = (drvRequirements as { source?: string } | null)?.source
      ?? clientRecord.drvSource
      ?? 'iom_usa';
    const eerKcal = resolveClientEerDailyKcal(clientRecord);
    const baseContext: ManualNutritionDrvContext = {
      drvRequirements,
      primaryTypes: undefined,
      clientGoals: clientRecord.clientGoals ?? null,
      ...(typeof eerKcal === 'number' && Number.isFinite(eerKcal) && eerKcal > 0 ? { eerKcal } : {}),
    };

    setAnalysisDrvContext(baseContext);

    (async () => {
      try {
        const drvResponse = await getDrvPrimaryTypes({
          source: String(source || 'iom_usa'),
          limit: 200,
        });
        if (cancelled) return;

        setAnalysisDrvContext({
          ...baseContext,
          primaryTypes: drvResponse.items ?? [],
        });
      } catch {
        if (cancelled) return;
        setAnalysisDrvContext(baseContext);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client]);

  React.useEffect(() => {
    let cancelled = false;

    if (analysisDayNumber === null) {
      setSelectedAnalysis(null);
      setIsAnalysisLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setIsAnalysisLoading(true);

    (async () => {
      try {
        const loaded = await fetchNutritionalAnalysisDayData(data.id, analysisDayNumber);
        if (cancelled) return;

        if (loaded) {
          const drvForAnalysis = loaded.mealPlanTargetDailyActive
            ? {
                ...(analysisDrvContext ?? {
                  drvRequirements: null,
                  primaryTypes: undefined as ManualNutritionDrvContext['primaryTypes'],
                }),
                mealPlanDailyMacroTargets: loaded.macroTargets,
              }
            : analysisDrvContext ?? null;
          setSelectedAnalysis(buildLoadedDayAnalysisSnapshot(loaded, drvForAnalysis));
          return;
        }
      } catch {
        // Fall back to local snapshot builder below.
      } finally {
        if (!cancelled) {
          setIsAnalysisLoading(false);
        }
      }

      if (cancelled) return;
      const fallbackDay = data.days.find((day) => day.day === analysisDayNumber) || null;
      setSelectedAnalysis(fallbackDay ? buildDayAnalysis(fallbackDay, targetDaily) : null);
    })();

    return () => {
      cancelled = true;
    };
  }, [analysisDayNumber, analysisDrvContext, data.days, data.id, targetDaily]);

  const filteredGenericTemplateOptions = React.useMemo(() => {
    const needle = templatePickerSearch.trim().toLowerCase();
    if (!needle) return genericTemplateOptions;

    return genericTemplateOptions.filter((option) => {
      if (option.name.toLowerCase().includes(needle)) return true;
      if (option.detail.toLowerCase().includes(needle)) return true;
      return option.tags.some((tag) => tag.label.toLowerCase().includes(needle));
    });
  }, [templatePickerSearch]);

  const isTemplatePickerOpen = Boolean(templatePickerAnchorEl);
  const handleImportGenericTemplate = React.useCallback(
    async (template: GenericTemplateOption) => {
      setImportingGenericTemplateId(template.id);
      const restrictionWarning = buildMealPlanRestrictionWarning(template, client, 'template');
      if (restrictionWarning) {
        showWarningToast('Check client restrictions', restrictionWarning.message);
      }
      try {
        const response = await useMealPlanTemplate(template.id, {
          newStartDate: data.searchParams.startDate,
          clientId: data.clientId || undefined,
          name: template.name,
        });
        if (!response.success) {
          throw new Error(response.message || 'Unable to apply template');
        }
        setGenericTemplateSearch(template.name);
        setTemplatePickerAnchorEl(null);
        setActivePlanTab('day');
        showSuccessToast('Template applied');
        await onMealPlanUpdated?.();
      } catch (error) {
        showErrorToast(error, 'Unable to apply template');
      } finally {
        setImportingGenericTemplateId(null);
      }
    },
    [client, data.clientId, data.searchParams.startDate, onMealPlanUpdated],
  );
  const templateMealSlotCount = React.useMemo(
    () => data.days.reduce((count, day) => count + getMealEntries(day).length, 0),
    [data.days],
  );
  const templateTotalCalories = React.useMemo(
    () =>
      data.days.reduce((sum, day) => {
        const calories = Number(day.dayTotals?.macros?.calories) || 0;
        return sum + Math.max(0, calories);
      }, 0),
    [data.days],
  );
  const handleSaveBoardTemplate = React.useCallback(
    async (payload: SaveAsTemplatePayload) => {
      trackMealPlanTemplateSaveClicked({
        product_area: 'meal_plans',
        meal_plan_surface: 'board_view',
        template_target: 'meal_plan',
        plan_status: isDraftPlan ? 'draft' : 'finalized',
      });
      try {
        const response = isDraftPlan
          ? await saveDraftMealPlanAsTemplate(data.id, {
              templateName: payload.templateName,
              description: payload.description,
              suitableFor: payload.suitableFor,
              tags: payload.clinicalTags,
            })
          : await saveFinalizedMealPlanAsTemplate(data.id, {
              templateName: payload.templateName,
              description: payload.description,
              suitableFor: payload.suitableFor,
              tags: payload.clinicalTags,
            });

        if (!response?.success) {
          throw new Error(response?.message || 'Unable to save meal plan template');
        }

        showSuccessToast('Meal plan saved as template');
      } catch (error) {
        showErrorToast(error, 'Unable to save meal plan template');
        throw error;
      }
    },
    [data.id, isDraftPlan],
  );

  const handleSaveSingleDayName = React.useCallback(
    async (dayNumber: number) => {
      const pending = unsavedDayNames[dayNumber];
      if (typeof pending !== 'string') {
        setEditingDayNumber(null);
        return;
      }

      const trimmed = pending.trim();
      const dayNameToSave = trimmed.length ? trimmed : null;
      const currentDayName = dayByNumber.get(dayNumber)?.dayName?.trim() || null;
      if (currentDayName === dayNameToSave) {
        setUnsavedDayNames((prev) => {
          const next = { ...prev };
          delete next[dayNumber];
          return next;
        });
        setEditingDayNumber(null);
        return;
      }

      const didSave = await onSaveDayName(dayNumber, dayNameToSave);
      if (!didSave) return;

      setUnsavedDayNames((prev) => {
        const next = { ...prev };
        delete next[dayNumber];
        return next;
      });
      setEditingDayNumber(null);
    },
    [dayByNumber, onSaveDayName, unsavedDayNames]
  );

  const handleCancelSingleDayName = React.useCallback((dayNumber: number) => {
    setUnsavedDayNames((prev) => {
      if (typeof prev[dayNumber] !== 'string') return prev;
      const next = { ...prev };
      delete next[dayNumber];
      return next;
    });
    setEditingDayNumber(null);
  }, []);

  React.useEffect(() => {
    if (editingDayNumber === null) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const activeHeader = dayHeaderRefs.current[editingDayNumber];
      const target = event.target;
      if (!activeHeader || !(target instanceof Node)) return;
      if (activeHeader.contains(target)) return;
      handleCancelSingleDayName(editingDayNumber);
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [editingDayNumber, handleCancelSingleDayName]);

  const handleOpenDeleteDayDialog = React.useCallback((dayNumber: number) => {
    setPendingDeleteDayNumber(dayNumber);
  }, []);

  const handleCloseDeleteDayDialog = React.useCallback(() => {
    setPendingDeleteDayNumber(null);
  }, []);

  const handleConfirmDeleteDay = React.useCallback(async () => {
    if (pendingDeleteDayNumber === null) return;
    const didDelete = await onDeleteDay(pendingDeleteDayNumber);
    if (didDelete) setPendingDeleteDayNumber(null);
  }, [onDeleteDay, pendingDeleteDayNumber]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      // Keep Enter for keyboard drag; Space must remain available for text fields
      // (notes dialog portals still bubble key events through the React tree).
      keyboardCodes: {
        start: ['Enter'],
        cancel: ['Escape'],
        end: ['Enter', 'Tab'],
      },
    })
  );

  const [activeDayData, setActiveDayData] = React.useState<any | null>(null);
  const [activeMealData, setActiveMealData] = React.useState<any | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    if (readOnly) return;
    const { active } = event;
    const type = active.data.current?.type;
    setActiveId(active.id as string);
    setActiveType(type as 'Day' | 'Meal' | null);

    if (type === 'Day') {
      const day = data.days.find(d => d.day === active.data.current?.day);
      setActiveDayData(day || null);
    } else if (type === 'Meal') {
      const day = data.days.find(d => d.day === active.data.current?.dayNumber);
      if (day && day.meals) {
        const entries = getMealEntries(day);
        const entry = entries.find(e => e.key === active.data.current?.mealKey);

        setActiveMealData({
          key: active.data.current?.mealKey,
          label: entry?.label || 'Meal',
          timeLabel: entry?.timeLabel || '',
          rows: entry ? buildRowsForMeal(entry) : [],
          isExpanded: isMealExpanded(day.day, active.data.current?.mealKey as string),
        });
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    setActiveType(null);
    setActiveDayData(null);
    setActiveMealData(null);
    if (readOnly) return;

    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (!activeData || !overData) return;

    if (activeData.type === 'Day' && overData.type === 'Day' && active.id !== over.id) {
      if (editingDayNumber !== null || isDeletingDay || isReorderingDays || isReorderingMeals) return;

      const activeDayNumber = activeData.day;
      const overDayNumber = overData.day;

      const currentDayOrder = data.days.map((day) => day.day);
      const oldIndex = currentDayOrder.indexOf(activeDayNumber);
      const newIndex = currentDayOrder.indexOf(overDayNumber);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Standard shifting logic (using arrayMove to match list strategy)
        const nextDayOrder = arrayMove(currentDayOrder, oldIndex, newIndex);
        await onReorderDays(nextDayOrder);
      }
    } else if (activeData.type === 'Meal' && overData.type === 'Meal' && active.id !== over.id) {
      if (isReorderingMeals) return;

      const activeDayNumber = activeData.dayNumber;
      const overDayNumber = overData.dayNumber;

      // Restrict dragging to the same day
      if (activeDayNumber !== overDayNumber) return;

      const activeMealKey = activeData.mealKey;
      const overMealKey = overData.mealKey;

      const dayObj = data.days.find((d) => d.day === activeDayNumber);
      if (!dayObj || !dayObj.meals) return;

      const entries = getMealEntries(dayObj).map((entry) => ({
        key: entry.key,
        slotOrder: entry.slotOrder,
      }));

      const oldIndex = entries.findIndex((e) => e.key === activeMealKey);
      const newIndex = entries.findIndex((e) => e.key === overMealKey);

      if (oldIndex !== -1 && newIndex !== -1) {
        const activeEntry = entries[oldIndex];
        const overEntry = entries[newIndex];

        // Standard shifting logic (using arrayMove to match verticalListSortingStrategy)
        const nextEntries = arrayMove(entries, oldIndex, newIndex);

        // Map the new key order back to the original slotOrder values
        const orderByKey = new Map(entries.map((entry) => [entry.key, entry.slotOrder]));
        const orderedMealOrders = nextEntries.map((e) => orderByKey.get(e.key) as number);

        await onReorderMealsInDay(activeDayNumber, orderedMealOrders, {
          firstMealName: activeMealKey,
          secondMealName: overMealKey,
          firstMealOrder: activeEntry?.slotOrder,
          secondMealOrder: overEntry?.slotOrder,
        });
      }
    }
  };

  const isMealDefaultExpanded = React.useCallback((dayNumber: number, mealKey: string) => {
    const dayObj = data.days.find(d => d.day === dayNumber);
    if (!dayObj) return false;
    const entries = getMealEntries(dayObj);
    const index = entries.findIndex(e => e.key === mealKey);
    return index === 0 || index === 1;
  }, [data.days]);

  const getMealToggleKey = React.useCallback((dayNumber: number, mealKey: string) => {
    return `${dayNumber}::${mealKey}`;
  }, []);

  const isMealExpanded = React.useCallback(
    (dayNumber: number, mealKey: string) => {
      const toggleKey = getMealToggleKey(dayNumber, mealKey);
      const storedValue = expandedMealRows[toggleKey];
      if (typeof storedValue === 'boolean') return storedValue;
      return isMealDefaultExpanded(dayNumber, mealKey);
    },
    [expandedMealRows, getMealToggleKey, isMealDefaultExpanded]
  );

  const toggleMealRow = React.useCallback(
    (dayNumber: number, mealKey: string) => {
      const toggleKey = getMealToggleKey(dayNumber, mealKey);
      setExpandedMealRows((prev) => {
        const current = typeof prev[toggleKey] === 'boolean' ? prev[toggleKey] : isMealDefaultExpanded(dayNumber, mealKey);
        return {
          ...prev,
          [toggleKey]: !current,
        };
      });
    },
    [getMealToggleKey, isMealDefaultExpanded]
  );

  React.useEffect(() => {
    setActivePlanTab(planMode === 'generic' ? 'generic' : 'day');
    setGenericTemplateSearch('');
    setTemplatePickerSearch('');
    setTemplatePickerAnchorEl(null);
    setIsGenericSectionVisible(planMode === 'generic');
    setUnsavedDayNames({});
    setEditingDayNumber(null);
  }, [data.id, planMode]);

  React.useEffect(() => {
    if (activePlanTab !== 'generic') {
      setTemplatePickerAnchorEl(null);
    }
  }, [activePlanTab]);

  React.useEffect(() => {
    setTemplatePickerPage(1);
  }, [templatePickerSearch]);

  React.useEffect(() => {
    if (activePlanTab !== 'generic' || planMode === 'generic') return;

    let cancelled = false;
    setIsGenericTemplateLoading(true);
    setGenericTemplateError('');
    void Promise.all([
      listMealPlanTemplates({
        page: templatePickerPage,
        pageSize: TEMPLATE_PICKER_PAGE_SIZE,
        templateKind: 'meal_plan',
        search: templatePickerSearch.trim() || undefined,
      }),
      listMealPlanTemplates({
        page: templatePickerPage,
        pageSize: TEMPLATE_PICKER_PAGE_SIZE,
        templateKind: 'meal_slot',
        search: templatePickerSearch.trim() || undefined,
      }),
    ])
      .then(([mealPlansResponse, mealSlotsResponse]) => {
        if (cancelled) return;
        if (!mealPlansResponse.success) {
          throw new Error(mealPlansResponse.message || 'Unable to load meal plan templates');
        }
        if (!mealSlotsResponse.success) {
          throw new Error(mealSlotsResponse.message || 'Unable to load meal slot templates');
        }
        const mergedTemplates = [
          ...(mealPlansResponse.data?.templates ?? []),
          ...(mealSlotsResponse.data?.templates ?? []),
        ];
        const totalCount =
          readTemplateTotalCount(mealPlansResponse.data?.pagination)
          + readTemplateTotalCount(mealSlotsResponse.data?.pagination);
        const totalPages = Math.max(
          readTemplateTotalPages(mealPlansResponse.data?.pagination),
          readTemplateTotalPages(mealSlotsResponse.data?.pagination),
          1,
        );
        setGenericTemplateOptions(mergedTemplates.map(toGenericTemplateOption));
        setTemplatePickerTotalCount(totalCount);
        setTemplatePickerTotalPages(totalPages);
      })
      .catch((error) => {
        if (cancelled) return;
        setGenericTemplateOptions([]);
        setTemplatePickerTotalCount(0);
        setTemplatePickerTotalPages(1);
        setGenericTemplateError(error instanceof Error ? error.message : 'Unable to load templates');
      })
      .finally(() => {
        if (!cancelled) setIsGenericTemplateLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activePlanTab, planMode, templatePickerPage, templatePickerSearch]);

  const MAX_DAYS = 7;
  const isMaxDaysReached = data.days.length >= MAX_DAYS;
  const normalizedGenerationType = String(data.generationType ?? '').trim().toLowerCase();
  const normalizedGenerationSource = String(
    (data as { generationSource?: string }).generationSource ?? ''
  ).trim().toLowerCase();
  const normalizedAiProvider = String((data as { aiProvider?: string }).aiProvider ?? '').trim().toLowerCase();
  const isAIAssistedPlan = normalizedGenerationType === 'ai_assisted';
  const hideDayDragHandleForAiAssistedPlan =
    isAIAssistedPlan &&
    (!normalizedGenerationSource || normalizedGenerationSource === 'ai') &&
    (!normalizedAiProvider || normalizedAiProvider === 'openai');
  const canShowAddDayUi = planMode !== 'generic' && !isAIAssistedPlan && !hideAddDayUi && !readOnly;

  const handleAttemptAddDay = React.useCallback((event?: React.MouseEvent | React.KeyboardEvent) => {
    if (event) event.preventDefault();
    if (!canShowAddDayUi || isMaxDaysReached) return;
    handleOpenAddDayDialog();
  }, [canShowAddDayUi, isMaxDaysReached, handleOpenAddDayDialog]);

  // Finalize affordance: only for manual drafts, and only once the parent wires onFinalize.
  const canShowFinalize = Boolean(onFinalize) && isDraftPlan && planCategory === 'manual' && !readOnly;

  // A day is "empty" when none of its meals contain a recipe. Finalizing such a
  // plan makes the backend reject the request, so we catch it client-side first.
  const emptyDays = React.useMemo(() => {
    if (!canShowFinalize) return [] as { day: number; label: string }[];
    return data.days
      .filter((day) => getMealEntries(day).reduce((total, entry) => total + entry.recipes.length, 0) === 0)
      .map((day) => ({ day: day.day, label: day.dayName?.trim() || `Day ${day.day}` }));
  }, [canShowFinalize, data.days]);

  const [isFinalizeValidationOpen, setIsFinalizeValidationOpen] = React.useState(false);

  const handleAttemptFinalize = React.useCallback(() => {
    if (!onFinalize || isFinalizing) return;
    if (emptyDays.length > 0) {
      setIsFinalizeValidationOpen(true);
      return;
    }
    void onFinalize();
  }, [emptyDays.length, isFinalizing, onFinalize]);

  return (
    <Box
      data-demo-id="meal-plan-board"
      sx={{
        mt: 2.2,
        minHeight: fillViewport ? 'calc(100vh - 7.5rem)' : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: '18px',
          border: '1px solid #e4eaee',
          bgcolor: '#f5f7f9',
          p: { xs: 1.2, md: 1.8 },
          minHeight: fillViewport ? '100%' : undefined,
          flex: fillViewport ? 1 : undefined,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 1.2,
            flexDirection: { xs: 'column', md: 'row' },
            mb: 1.7,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Button
              onClick={onBack}
              disableRipple
              sx={{
                minHeight: 31,
                px: 1.1,
                borderRadius: '8px',
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#475467',
                border: '1px solid #d8e1ea',
                bgcolor: '#fff',
                '&:hover': { bgcolor: '#fff' },
              }}
            >
              Back
            </Button>

            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                fontWeight: 800,
                color: '#2f4858',
                letterSpacing: '0.05em',
              }}
            >
              DIET PLAN NAME:
            </Typography>

            <Tag
              variant="outlined"
              size="md"
              className="h-[31px] border-[#EAECF0] bg-white font-[Inter,sans-serif] text-[0.82rem] font-medium text-[#475467]"
            >
              {planName || planDurationLabel}
            </Tag>

            {canEditNotes || planPractitionerNotes ? (
              <MealPlanNotesControl
                value={planPractitionerNotes}
                disabled={isSavingMealPlanNotes}
                editable={canEditNotes}
                variant="icon"
                size="sm"
                label="Meal plan note"
                emptyLabel="Add a note for this meal plan"
                helperText="Optional tip or reminder. This can also show on the PDF export."
                placeholder="Enter meal plan notes"
                onSave={async (notes) => {
                  if (!onSaveMealPlanNotes) return false;
                  return onSaveMealPlanNotes({ level: 'plan', notes });
                }}
              />
            ) : null}
          </Box>

          <Stack direction="row" spacing={0.8} flexWrap="wrap" alignItems="center">
            {canShowExportPdf ? (
              <Button
                variant="outlined"
                startIcon={
                  isExportingPdf ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: 'currentColor' }} />
                  ) : (
                    <PrintOutlinedIcon sx={{ fontSize: 17 }} />
                  )
                }
                onClick={onExportPdf}
                disabled={isExportingPdf}
                sx={{
                  textTransform: 'none',
                  minHeight: 34,
                  minWidth: 128,
                  borderRadius: '10px',
                  borderColor: isExportingPdf ? '#20AEDC' : '#d0d5dd',
                  bgcolor: isExportingPdf ? '#EAF7FC' : '#fff',
                  color: isExportingPdf ? '#127EA5' : '#475467',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.8rem',
                  '&:hover': { borderColor: '#d0d5dd', bgcolor: '#fff' },
                  '&.Mui-disabled': {
                    borderColor: '#20AEDC',
                    bgcolor: '#EAF7FC',
                    color: '#127EA5',
                  },
                }}
              >
                {isExportingPdf ? 'Exporting...' : 'Export PDF'}
              </Button>
            ) : null}
            {activePlanTab === 'day' && canShowAddDayUi ? (
              <Tooltip
                title={isMaxDaysReached ? 'Maximum 7 days allowed in a plan' : ''}
                placement="top"
                arrow
                disableHoverListener={!isMaxDaysReached}
              >
                <Box sx={{ display: 'inline-block' }}>
                  <Button
                    data-demo-id="meal-plan-add-day"
                    variant="contained"
                    endIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
                    onClick={handleAttemptAddDay}
                    disabled={isMaxDaysReached}
                    sx={{
                      alignSelf: { xs: 'flex-start', md: 'auto' },
                      height: 50,
                      minWidth: 160,
                      px: 2.2,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '1.05rem',
                      fontWeight: 500,
                      bgcolor: '#2aabd7',
                      color: '#fff',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#249cc5',
                        boxShadow: 'none',
                      },
                      '&.Mui-disabled': {
                        bgcolor: '#e2e8f0',
                        color: '#94a3b8',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Add Day
                  </Button>
                </Box>
              </Tooltip>
            ) : null}
            {canShowFinalize ? (
              <Button
                data-demo-id="meal-plan-finalize"
                variant="contained"
                startIcon={
                  isFinalizing ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: 'currentColor' }} />
                  ) : (
                    <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                  )
                }
                onClick={handleAttemptFinalize}
                disabled={isFinalizing}
                sx={{
                  alignSelf: { xs: 'flex-start', md: 'auto' },
                  height: 50,
                  minWidth: 170,
                  px: 2.2,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1.05rem',
                  fontWeight: 500,
                  bgcolor: '#2aabd7',
                  color: '#fff',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#249cc5', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
                }}
              >
                {isFinalizing ? 'Finalising…' : 'Finalise Plan'}
              </Button>
            ) : null}
          </Stack>
        </Box>

        {activePlanTab === 'generic' ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: '18px',
              border: '1px solid #dbe5ee',
              bgcolor: '#fff',
              p: 1.2,
              minHeight: { xs: 520, md: 620 },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 1,
              }}
            >
              <TextField
                value={genericTemplateSearch}
                onChange={(event) => setGenericTemplateSearch(event.target.value)}
                onClick={(event) => {
                  if (planMode === 'generic') return;
                  setTemplatePickerSearch(genericTemplateSearch.trim());
                  setTemplatePickerAnchorEl(event.currentTarget as HTMLElement);
                }}
                placeholder="Search for Templates"
                size="small"
                sx={{
                  width: { xs: '100%', md: 270 },
                  '& .MuiOutlinedInput-root': {
                    height: 34,
                    borderRadius: '999px',
                    bgcolor: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.82rem',
                    color: '#667085',
                    '& fieldset': { borderColor: '#e3e8ef' },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: '#98a2b3', fontSize: 17 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        component="span"
                        sx={{
                          minWidth: 20,
                          px: 0.4,
                          py: 0.05,
                          borderRadius: 999,
                          border: '1px solid #eaecf0',
                          color: '#98a2b3',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem',
                          textAlign: 'center',
                          lineHeight: 1.4,
                        }}
                      >
                        /
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" spacing={0.8} flexWrap="wrap">
                {!hidePlanTabs && planMode !== 'generic' ? (
                  <Button
                    variant="outlined"
                    startIcon={<BookmarkBorderRoundedIcon sx={{ fontSize: 17 }} />}
                    onClick={() => setIsSaveTemplateOpen(true)}
                    sx={{
                      textTransform: 'none',
                      minHeight: 34,
                      borderRadius: '10px',
                      borderColor: '#d0d5dd',
                      color: '#475467',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.8rem',
                      '&:hover': { borderColor: '#d0d5dd', bgcolor: '#fff' },
                    }}
                  >
                    Save as Template
                  </Button>
                ) : null}
                {canShowExportPdf ? (
                  <Button
                    variant="outlined"
                    startIcon={
                      isExportingPdf ? (
                        <CircularProgress size={16} thickness={5} sx={{ color: 'currentColor' }} />
                      ) : (
                        <PrintOutlinedIcon sx={{ fontSize: 17 }} />
                      )
                    }
                    onClick={onExportPdf}
                    disabled={isExportingPdf}
                    sx={{
                      textTransform: 'none',
                      minHeight: 34,
                      minWidth: 128,
                      borderRadius: '10px',
                      borderColor: isExportingPdf ? '#20AEDC' : '#d0d5dd',
                      bgcolor: isExportingPdf ? '#EAF7FC' : '#fff',
                      color: isExportingPdf ? '#127EA5' : '#475467',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.8rem',
                      '&:hover': { borderColor: '#d0d5dd', bgcolor: '#fff' },
                      '&.Mui-disabled': {
                        borderColor: '#20AEDC',
                        bgcolor: '#EAF7FC',
                        color: '#127EA5',
                      },
                    }}
                  >
                    {isExportingPdf ? 'Exporting...' : 'Export PDF'}
                  </Button>
                ) : null}
                {!hideGenericToolbarAddMeal ? (
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
                    disabled={isMaxDaysReached}
                    onClick={isMaxDaysReached ? undefined : () => setIsGenericSectionVisible(true)}
                    sx={{
                      textTransform: 'none',
                      minHeight: 34,
                      px: 1.5,
                      borderRadius: '10px',
                      bgcolor: '#25a8da',
                      color: '#fff',
                      boxShadow: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.82rem',
                      '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
                      '&.Mui-disabled': {
                        bgcolor: '#e2e8f0',
                        color: '#94a3b8',
                        boxShadow: 'none',
                      }
                    }}
                  >
                    Add Meal Plan
                  </Button>
                ) : null}
              </Stack>
            </Box>

            {isGenericSectionVisible ? (
              <Box sx={{ mt: 1.25, flex: 1, minHeight: 0 }}>
                {planMode === 'generic' ? (
                  <GenericMealPlanNutritionPage
                    planId={data.id}
                    clientId=""
                    dayNumber={data.days[0]?.day || 1}
                    onBack={onBack}
                    embedded
                    showClientSummaryStrip={false}
                    onMealPlanUpdated={onMealPlanUpdated}
                  />
                ) : (
                  <NutritionalAnalysisPage
                    planId={data.id}
                    clientId={client?.id}
                    dayNumber={data.days[0]?.day || 1}
                    onBack={() => {
                      setActivePlanTab('day');
                    }}
                    embedded
                    onMealPlanUpdated={onMealPlanUpdated}
                  />
                )}
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center' }}>
                <Stack alignItems="center" spacing={1.1} sx={{ textAlign: 'center', maxWidth: 420, px: 2 }}>
                  <EmojiFoodBeverageRoundedIcon sx={{ fontSize: 34, color: '#2aabd7' }} />
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '2.2rem', fontWeight: 700, color: '#344054' }}>
                    No meals added yet
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: '#475467', lineHeight: 1.5 }}>
                    Search and load a template, or click "Add meal plan" to start building this cohort meal plan from scratch.
                  </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
                  disabled={isMaxDaysReached}
                  onClick={isMaxDaysReached ? undefined : () => setIsGenericSectionVisible(true)}
                  sx={{
                    mt: 0.9,
                    minHeight: 46,
                    minWidth: 190,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '1.1rem',
                    bgcolor: '#2aabd7',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#249cc5', boxShadow: 'none' },
                    '&.Mui-disabled': {
                      bgcolor: '#e2e8f0',
                      color: '#94a3b8',
                      boxShadow: 'none',
                    }
                  }}
                >
                  Add Meal Plan
                </Button>
                  {isMaxDaysReached && (
                    <Typography sx={{ mt: 1, fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>
                      Maximum 7 days allowed in a plan
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            <Popover
              open={isTemplatePickerOpen}
              anchorEl={templatePickerAnchorEl}
              onClose={() => setTemplatePickerAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  mt: 0.7,
                  borderRadius: '16px',
                  border: '1px solid #e6ebf0',
                  boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
                  width: { xs: 'calc(100vw - 30px)', sm: 430 },
                  maxWidth: 'calc(100vw - 30px)',
                  overflow: 'hidden',
                },
              }}
            >
              <Box sx={{ px: 1.1, pt: 0.75, pb: 0.85 }}>
                <TextField
                  value={templatePickerSearch}
                  onChange={(event) => setTemplatePickerSearch(event.target.value)}
                  placeholder="Search templates..."
                  size="small"
                  autoFocus
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      borderRadius: '14px',
                      bgcolor: '#fff',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.75rem',
                      color: '#475467',
                      '& fieldset': { borderColor: '#d7dee7', borderWidth: '1.5px' },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: '#98a2b3', fontSize: 14 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ mt: 0.45, borderTop: '1px solid #e9edf3' }} />

                <Stack spacing={0} sx={{ mt: 0.3, maxHeight: 360, overflowY: 'auto', pr: 0.2 }}>
                  {isGenericTemplateLoading ? (
                    <Box sx={{ py: 3.5, display: 'grid', placeItems: 'center' }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : genericTemplateError ? (
                    <Box sx={{ py: 3.5, textAlign: 'center' }}>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#b42318' }}>
                        {genericTemplateError}
                      </Typography>
                    </Box>
                  ) : null}
                  {!isGenericTemplateLoading && !genericTemplateError ? filteredGenericTemplateOptions.map((template, index) => (
                    <Box
                      key={template.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        void handleImportGenericTemplate(template);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          void handleImportGenericTemplate(template);
                        }
                      }}
                      sx={{
                        py: 0.78,
                        px: 0.35,
                        cursor: 'pointer',
                        borderTop: index === 0 ? 'none' : '1px solid #f1f4f8',
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        alignItems: 'center',
                        gap: 0.85,
                        opacity: importingGenericTemplateId === template.id ? 0.62 : 1,
                        pointerEvents: importingGenericTemplateId ? 'none' : 'auto',
                        '&:hover': { bgcolor: '#f8fafc' },
                        '&:focus-visible': {
                          outline: '2px solid #8fd6ec',
                          outlineOffset: 1,
                          borderRadius: '10px',
                        },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.55, minWidth: 0 }}>
                          <Typography
                            sx={{
                              minWidth: 0,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              color: '#1f2937',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {template.name}
                          </Typography>
                          <MealPlanRestrictionBadges source={template} maxVisible={2} />
                        </Box>

                        <Box sx={{ mt: 0.3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.38 }}>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3', lineHeight: 1.2 }}>
                            {template.detail}
                          </Typography>

                          {template.tags.map((tag) => (
                            <Tag
                              key={`${template.id}-${tag.label}`}
                              size="xs"
                              className="font-[Inter,sans-serif] text-[0.75rem] font-semibold leading-[1.2]"
                              sx={{ bgcolor: tag.bg, color: tag.color }}
                            >
                              {tag.label}
                            </Tag>
                          ))}
                        </Box>
                      </Box>

                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem',
                          color: '#23a6d2',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        }}
                      >
                        {importingGenericTemplateId === template.id
                          ? 'Importing...'
                          : template.kcal > 0
                            ? `${template.kcal.toLocaleString()} kcal`
                            : 'Import'}
                      </Typography>
                    </Box>
                  )) : null}
                </Stack>

                {!isGenericTemplateLoading && !genericTemplateError && !filteredGenericTemplateOptions.length ? (
                  <Box sx={{ py: 3.5, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#667085' }}>
                      No matching templates found.
                    </Typography>
                  </Box>
                ) : null}

                {!isGenericTemplateLoading && !genericTemplateError && templatePickerTotalPages > 1 ? (
                  <Box
                    sx={{
                      mt: 0.55,
                      pt: 0.7,
                      borderTop: '1px solid #edf1f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.7,
                    }}
                  >
                    <Button
                      size="small"
                      disabled={templatePickerPage <= 1}
                      onClick={() => setTemplatePickerPage((prev) => Math.max(1, prev - 1))}
                      sx={{
                        minWidth: 62,
                        minHeight: 28,
                        borderRadius: '10px',
                        border: '1px solid #d0d9e3',
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        color: '#475467',
                      }}
                    >
                      Previous
                    </Button>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#667085' }}>
                      Page {templatePickerPage} of {templatePickerTotalPages}
                      {templatePickerTotalCount > 0 ? ` · ${templatePickerTotalCount} templates` : ''}
                    </Typography>
                    <Button
                      size="small"
                      disabled={templatePickerPage >= templatePickerTotalPages}
                      onClick={() => setTemplatePickerPage((prev) => Math.min(templatePickerTotalPages, prev + 1))}
                      sx={{
                        minWidth: 44,
                        minHeight: 28,
                        borderRadius: '10px',
                        border: '1px solid #d0d9e3',
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        color: '#475467',
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Popover>
          </Paper>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={data.days.map((day) => `day-${day.day}`)}
              strategy={horizontalListSortingStrategy}
            >
              <Box
                sx={{
                  minHeight: fillViewport ? 0 : undefined,
                  flex: fillViewport ? 1 : undefined,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridAutoColumns: hidePlanTabs
                      ? {
                        xs: 'minmax(360px, 94%)',
                        md: data.days.length <= 1 ? '340px' : 'calc((100% - 3.9rem) / 4)',
                      }
                      : { xs: 'minmax(330px, 92%)', md: '340px' },
                    alignItems: 'start',
                    gap: 1.3,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    pb: 0.55,
                    minHeight: fillViewport ? 0 : undefined,
                    flex: fillViewport ? 1 : undefined,
                  }}
                >
              {data.days.map((day) => {
                const originalDayTitle = day.dayName?.trim() || `Day ${day.day}`;
                const draftDayTitle = unsavedDayNames[day.day];
                const dayTitle = typeof draftDayTitle === 'string' ? normalizeDayTitle(draftDayTitle, day.day) : originalDayTitle;
                const dayTitleInput = typeof draftDayTitle === 'string' ? draftDayTitle : day.dayName?.trim() || '';
                const isEditingThisDay = editingDayNumber === day.day;
                const canDragDayCard =
                  !readOnly &&
                  !hideDayDragHandleForAiAssistedPlan &&
                  !isEditingThisDay &&
                  editingDayNumber === null &&
                  !isDeletingDay &&
                  !isReorderingDays &&
                  !isReorderingMeals;
                const macros = day.dayTotals?.macros;
                const calories = Math.max(0, Number(macros?.calories) || 0);
                const protein = Math.max(0, Number(macros?.protein) || 0);
                const carbs = Math.max(0, Number(macros?.carbs) || 0);
                const fat = Math.max(0, Number(macros?.fat) || 0);

                const caloriesTarget = Math.max(1, Number(targetDaily.calories ?? 2000) || 2000);
                const proteinTarget = Math.max(1, resolveRangeMidpoint(targetDaily.proteinMin, targetDaily.proteinMax, 160) ?? 160);
                const carbsTarget = Math.max(1, resolveRangeMidpoint(targetDaily.carbsMin, targetDaily.carbsMax, 160) ?? 160);
                const fatTarget = Math.max(1, resolveRangeMidpoint(targetDaily.fatMin, targetDaily.fatMax, 85) ?? 85);

                const mealEntries = getMealEntries(day);

                const macroCards = [
                  {
                    key: 'calories',
                    label: 'Calories',
                    value: calories,
                    unit: 'kcal' as const,
                    target: caloriesTarget,
                    progress: getProgressValue(calories, caloriesTarget),
                  },
                  {
                    key: 'carbs',
                    label: 'Carbs',
                    value: carbs,
                    unit: 'g' as const,
                    target: carbsTarget,
                    progress: getProgressValue(carbs, carbsTarget),
                  },
                  {
                    key: 'protein',
                    label: 'Protein',
                    value: protein,
                    unit: 'g' as const,
                    target: proteinTarget,
                    progress: getProgressValue(protein, proteinTarget),
                  },
                  {
                    key: 'fat',
                    label: 'Fat',
                    value: fat,
                    unit: 'g' as const,
                    target: fatTarget,
                    progress: getProgressValue(fat, fatTarget),
                  },
                ];

                return (
                  <SortableDayWrapper key={`day-card-${day.day}`} id={`day-${day.day}`} day={day.day} disabled={!canDragDayCard}>
                    {({ setNodeRef: setDayNodeRef, attributes: dayAttributes, listeners: dayListeners, style: dayStyle, isDragging: isDayDragging }) => (
                      <Paper
                        ref={setDayNodeRef}
                        style={dayStyle}
                        elevation={0}
                        sx={{
                          alignSelf: 'start',
                          borderRadius: '16px',
                          border: isDayDragging ? '2px dashed #25a8da' : '1px solid #e2e8ed',
                          bgcolor: isDayDragging ? '#f0f9ff' : '#fff',
                          overflow: 'hidden',
                          opacity: isDayDragging ? 0.4 : 1,
                          position: 'relative',
                          zIndex: isDayDragging ? 100 : 1,
                        }}
                      >
                        <Box
                          ref={(node: HTMLDivElement | null) => {
                            dayHeaderRefs.current[day.day] = node;
                          }}
                          sx={{
                            px: 1.5,
                            py: 1.35,
                            bgcolor: '#35add2',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box
                            {...dayAttributes}
                            {...dayListeners}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.65,
                              minWidth: 0,
                              cursor: canDragDayCard ? 'grab' : 'default',
                              outline: 'none',
                            }}
                          >
                            {!readOnly && !hideDayDragHandleForAiAssistedPlan ? (
                              <DragIndicatorRoundedIcon sx={{ fontSize: 18, opacity: 0.95 }} />
                            ) : null}
                            {isEditingThisDay && !readOnly ? (
                              <TextField
                                size="small"
                                autoFocus
                                value={dayTitleInput}
                                placeholder={`Day ${day.day}`}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setUnsavedDayNames((prev) => {
                                    const next = { ...prev };
                                    next[day.day] = value;
                                    return next;
                                  });
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Escape') {
                                    event.preventDefault();
                                    handleCancelSingleDayName(day.day);
                                  }
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    void handleSaveSingleDayName(day.day);
                                  }
                                }}
                                sx={{
                                  minWidth: 110,
                                  '& .MuiOutlinedInput-root': {
                                    height: 30,
                                    borderRadius: '8px',
                                    bgcolor: 'transparent',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: '#ffffff',
                                    '& fieldset': { borderColor: '#ffffff' },
                                    '&:hover fieldset': { borderColor: '#ffffff' },
                                    '&.Mui-focused fieldset': { borderColor: '#ffffff', borderWidth: '1.5px' },
                                  },
                                  '& .MuiInputBase-input': { py: '6px', px: '8px', color: '#ffffff' },
                                }}
                              />
                            ) : (
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', fontWeight: 600, lineHeight: '28px', letterSpacing: '0px', color: '#fff', whiteSpace: 'nowrap' }}>
                                {dayTitle}
                              </Typography>
                            )}
                          </Box>

                  {isEditingThisDay && !readOnly ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        onClick={() => {
                          void handleSaveSingleDayName(day.day);
                        }}
                        disabled={isSavingDayName}
                        startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          minHeight: 20,
                          px: 1.1,
                          borderRadius: '8px',
                          bgcolor: '#fff',
                          color: '#2da6d2',
                          textTransform: 'none',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#fff', boxShadow: 'none' },
                        }}
                      >
                        Save
                      </Button>
                    </Box>
                  ) : !readOnly ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {(canEditNotes || day.notes?.trim()) ? (
                        <MealPlanNotesControl
                          value={day.notes}
                          disabled={isSavingMealPlanNotes}
                          editable={canEditNotes}
                          variant="icon"
                          tone="onDark"
                          size="sm"
                          label="Day note"
                          emptyLabel="Add a note for this day"
                          helperText="Optional. Add guidance for this day only."
                          placeholder="Enter day notes"
                          onSave={async (notes) => {
                            if (!onSaveMealPlanNotes) return false;
                            return onSaveMealPlanNotes({ level: 'day', day: day.day, notes });
                          }}
                        />
                      ) : null}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (editingDayNumber !== null && editingDayNumber !== day.day) return;
                          setUnsavedDayNames((prev) => ({
                            ...prev,
                            [day.day]: day.dayName?.trim() || '',
                          }));
                          setEditingDayNumber(day.day);
                        }}
                        sx={{ color: '#d5f4ff', '&:hover': { bgcolor: 'transparent' } }}
                      >
                        <EditRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDayDialog(day.day)}
                        sx={{ color: '#d5f4ff', '&:hover': { bgcolor: 'transparent' } }}
                      >
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingDayNumber !== null && editingDayNumber !== day.day) return;
                          handleOpenCopyDayDialog(day.day);
                        }}
                        aria-label="Copy meals from another day"
                        sx={{ color: '#d5f4ff', '&:hover': { bgcolor: 'transparent' } }}
                      >
                        <ContentCopyRoundedIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Box>
                  ) : (canEditNotes || day.notes?.trim()) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MealPlanNotesControl
                        value={day.notes}
                        editable={false}
                        variant="icon"
                        tone="onDark"
                        size="sm"
                        label="Day note"
                      />
                    </Box>
                  ) : null}
                </Box>

                <Box sx={{ p: 1.2, pt: 1.25, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.78 }}>
                  {macroCards.map((card) => {
                    const trend = getMacroTrendFromSuggestedRda(card.value, card.target);
                    const TrendIcon = trend.Icon;
                    return (
                    <Box
                      key={card.key}
                      data-demo-id={card.key === 'calories' ? 'meal-plan-calorie-intake-card' : undefined}
                      sx={{
                        borderRadius: '12px',
                        border: '1px solid #eff4f7',
                        bgcolor: '#f4f7fa',
                        px: 0.9,
                        py: 1.05,
                        minHeight: 92,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.55, gap: 0.5, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, minWidth: 0 }}>
                          <DragIndicatorRoundedIcon sx={{ fontSize: 13, color: '#8797a7', flexShrink: 0 }} />
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '12px',
                              lineHeight: '16.5px',
                              color: '#72849a',
                              fontWeight: 500,
                              letterSpacing: '0px',
                              textTransform: 'capitalize',
                            }}
                          >
                            {card.label}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, flexShrink: 0 }}>
                          <TrendIcon sx={{ fontSize: 14, color: trend.color }} />
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: trend.color,
                              lineHeight: 1,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {trend.label}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          textAlign: 'center',
                          width: '100%',
                          minWidth: 0,
                          px: 0.25,
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.35, flexWrap: 'wrap' }}>
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '1.05rem',
                              fontWeight: 700,
                              color: '#1f2937',
                              lineHeight: 1.15,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {roundNutritionDisplay(card.value)}
                          </Typography>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', lineHeight: 1.2 }}>
                            {card.unit === 'kcal' ? 'kcal' : 'g'}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            mt: 0.35,
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                            color: '#8ea0b0',
                            fontWeight: 500,
                            lineHeight: 1.25,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          of {roundNutritionDisplay(card.target)} {card.unit === 'kcal' ? 'kcal' : 'g'}
                        </Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={card.progress}
                        sx={{
                          mt: 0.52,
                          height: 4,
                          borderRadius: 99,
                          bgcolor: '#EAECF0',
                          '& .MuiLinearProgress-bar': { bgcolor: trend.barColor },
                          width: '72%',
                          mx: 'auto',
                        }}
                      />
                    </Box>
                    );
                  })}
                </Box>

                <Box sx={{ px: 1.2, pb: 1.25, mt: 0.65 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.35 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        lineHeight: '18px',
                        letterSpacing: '0.6px',
                        color: '#111827',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      MEAL SCHEDULE
                    </Typography>
                  </Box>

                  <SortableContext
                    items={mealEntries.map((entry) => `meal-${day.day}-${entry.key}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Stack spacing={0.9}>
                      {mealEntries.map((entry, index) => {
                        const isExpanded = isMealExpanded(day.day, entry.key);

                        if (isExpanded) {
                          const rows = buildRowsForMeal(entry);
                          const genericOptionGroups = planMode === 'generic' ? buildGenericOptionGroups(rows) : [];
                          const selectedGenericGroupId = entry.selectedOptionGroupId || null;
                          const selectedGenericGroup = selectedGenericGroupId
                            ? genericOptionGroups.find((group) => group.optionGroupId === selectedGenericGroupId)
                            : undefined;
                          const ungroupedGenericRows = planMode === 'generic'
                            ? rows.filter((row) => !row.optionGroupId && getGenericOptionItemId(row))
                            : [];

                          return (
                            <SortableMealWrapper key={`expanded-${entry.key}`} id={`meal-${day.day}-${entry.key}`} dayNumber={day.day} mealKey={entry.key} disabled={readOnly}>
                              {({ setNodeRef, attributes, listeners, style, isDragging }) => (
                                <React.Fragment>
                                  {renderTimelineNode(
                                    <Box
                                      ref={setNodeRef}
                                      style={style}
                                      id={`meal-card-${day.day}-${entry.key}`}
                                      sx={{
                                        position: 'relative',
                                        zIndex: isDragging ? 100 : 1,
                                      }}
                                    >
                                      <Box
                                        {...attributes}
                                        {...listeners}
                                        sx={{
                                          borderRadius: '9px',
                                          bgcolor: '#26a8d6',
                                          border: 'none',
                                          color: '#fff',
                                          px: 0.9,
                                          py: 0.72,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: 0.8,
                                          cursor: readOnly ? 'default' : 'grab',
                                          userSelect: 'none',
                                          outline: 'none',
                                          boxShadow: isDragging ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' : 'none',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                                          {!readOnly ? (
                                            <DragIndicatorRoundedIcon sx={{ fontSize: 16, opacity: 0.95 }} />
                                          ) : null}
                                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.98rem', fontWeight: 500, color: '#fff', whiteSpace: 'nowrap' }}>
                                            {entry.label}
                                          </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65 }}>
                                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 500, color: '#fff', whiteSpace: 'nowrap' }}>
                                            {entry.timeLabel}
                                          </Typography>
                                          {(canEditNotes || entry.notes?.trim()) ? (
                                            <Box
                                              onClick={(event) => event.stopPropagation()}
                                              onPointerDown={(event) => event.stopPropagation()}
                                            >
                                              <MealPlanNotesControl
                                                value={entry.notes}
                                                disabled={isSavingMealPlanNotes}
                                                editable={canEditNotes}
                                                variant="icon"
                                                tone="onDark"
                                                size="sm"
                                                label="Meal note"
                                                emptyLabel="Add a note for this meal"
                                                helperText="Optional. Tips for this meal only."
                                                placeholder="Enter meal notes"
                                                onSave={async (notes) => {
                                                  if (!onSaveMealPlanNotes) return false;
                                                  return onSaveMealPlanNotes({
                                                    level: 'meal',
                                                    day: day.day,
                                                    mealName: entry.mealName,
                                                    notes,
                                                  });
                                                }}
                                              />
                                            </Box>
                                          ) : null}
                                          <IconButton
                                            size="small"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              toggleMealRow(day.day, entry.key);
                                            }}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            sx={{ p: 0.15, color: '#fff', '&:hover': { bgcolor: 'transparent' } }}
                                          >
                                            <KeyboardArrowUpRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                                          </IconButton>
                                        </Box>
                                      </Box>

                                      <Box
                                        sx={{
                                          mt: 0.65,
                                          borderRadius: '8px',
                                          border: '1px solid #d9e1e8',
                                          bgcolor: '#fff',
                                          pl: 1.45,
                                          pr: 1.1,
                                          pt: 1.35,
                                          pb: 1.2,
                                          minHeight: rows.length ? 'auto' : 76,
                                        }}
                                      >
                                        {rows.length ? (
                                          planMode === 'generic' ? (
                                            <Stack spacing={1.1}>
                                              {!readOnly && rows.length > 1 ? (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: { xs: 'stretch', sm: 'center' },
                                                    justifyContent: 'space-between',
                                                    gap: 0.8,
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    border: '1px dashed #93c5fd',
                                                    borderRadius: '10px',
                                                    bgcolor: '#eff6ff',
                                                    px: 1,
                                                    py: 0.85,
                                                  }}
                                                >
                                                  <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 800, color: '#1d4ed8' }}>
                                                      AND option
                                                    </Typography>
                                                    <Typography sx={{ mt: 0.15, fontFamily: 'Inter, sans-serif', fontSize: '0.72rem', color: '#475569' }}>
                                                      Group foods like idli + banana as one selectable option.
                                                    </Typography>
                                                  </Box>
                                                  <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={updatingGenericOptionItemId != null || ungroupedGenericRows.length < 2}
                                                    onClick={() => {
                                                      void handleCreateGenericAndOption(entry, rows, `Option ${genericOptionGroups.length + 1}`);
                                                    }}
                                                    sx={{
                                                      flexShrink: 0,
                                                      minWidth: 0,
                                                      px: 1,
                                                      py: 0.45,
                                                      borderRadius: '9px',
                                                      textTransform: 'none',
                                                      fontSize: '0.76rem',
                                                      fontWeight: 800,
                                                      bgcolor: '#2563eb',
                                                      boxShadow: 'none',
                                                      '&:hover': { bgcolor: '#1d4ed8', boxShadow: 'none' },
                                                    }}
                                                  >
                                                    Create AND option
                                                  </Button>
                                                </Box>
                                              ) : null}
                                              {genericOptionGroups.map((group, groupIndex) => {
                                                const isSelectedGroup = group.optionGroupId
                                                  ? selectedGenericGroupId === group.optionGroupId
                                                  : !selectedGenericGroupId && groupIndex === 0;
                                                return (
                                                  <Box
                                                    key={group.key}
                                                    sx={{
                                                      border: group.rows.length > 1 ? '1px solid #bfdbfe' : '1px solid #eef2f6',
                                                      borderRadius: '10px',
                                                      bgcolor: isSelectedGroup ? '#f0fdf4' : '#fff',
                                                      p: 0.9,
                                                    }}
                                                  >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                                                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', fontWeight: 700, color: '#475467' }}>
                                                          {group.rows.length > 1 ? group.optionGroupLabel : `Option ${groupIndex + 1}`}
                                                        </Typography>
                                                        {group.rows.length > 1 ? (
                                                          <Tag size="xs" variant="info" className="h-[18px] text-[0.68rem] font-extrabold text-[#1D4ED8]">
                                                            AND
                                                          </Tag>
                                                        ) : (
                                                          <Tag size="xs" variant="neutral" className="h-[18px] text-[0.68rem] font-extrabold text-[#64748B]">
                                                            OR
                                                          </Tag>
                                                        )}
                                                        {isSelectedGroup ? (
                                                          <Tag size="xs" variant="success" className="h-[18px] text-[0.68rem] font-extrabold text-[#15803D]">
                                                            Analysis
                                                          </Tag>
                                                        ) : null}
                                                      </Box>
                                                      {group.optionGroupId ? (
                                                        <Button
                                                          size="small"
                                                          disabled={updatingGenericOptionItemId != null || isSelectedGroup}
                                                          onClick={() => {
                                                            void handleUpdateGenericOptionGroup({
                                                              entry,
                                                              row: group.rows[0],
                                                              selectedOptionGroupId: group.optionGroupId,
                                                            });
                                                          }}
                                                          sx={{ minWidth: 0, px: 0.75, py: 0.2, textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, color: '#1498ca' }}
                                                        >
                                                          Use for analysis
                                                        </Button>
                                                      ) : null}
                                                    </Box>

                                                    <Stack spacing={0.9}>
                                                      {group.rows.map((row, rowIndex) => {
                                                        const itemId = getGenericOptionItemId(row);
                                                        const activeGroupId = selectedGenericGroupId || group.optionGroupId || `and-${Date.now()}`;
                                                        const activeGroupLabel = selectedGenericGroup?.optionGroupLabel
                                                          || (group.optionGroupId ? group.optionGroupLabel : `Option ${groupIndex + 1}`);
                                                        const isUpdatingThisItem = updatingGenericOptionItemId === itemId;
                                                        return (
                                                          <React.Fragment key={`${entry.key}-row-${group.key}-${rowIndex}`}>
                                                            {rowIndex > 0 ? (
                                                              <Typography sx={{ pl: 5, fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', fontWeight: 800, color: '#1d4ed8' }}>
                                                                AND
                                                              </Typography>
                                                            ) : null}
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                                              <Avatar
                                                                src={row.image}
                                                                alt={row.title}
                                                                variant="rounded"
                                                                sx={{ width: 34, height: 34, borderRadius: '8px', flexShrink: 0 }}
                                                                imgProps={{
                                                                  onError: (event: React.SyntheticEvent<HTMLImageElement>) => {
                                                                    const target = event.currentTarget;
                                                                    target.onerror = null;
                                                                    target.src = DEFAULT_RECIPE_IMAGE;
                                                                  },
                                                                }}
                                                              />
                                                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
                                                                  <Typography
                                                                    sx={{
                                                                      flex: 1,
                                                                      minWidth: 0,
                                                                      fontFamily: 'Inter, sans-serif',
                                                                      fontSize: '0.78rem',
                                                                      fontWeight: 600,
                                                                      color: '#344054',
                                                                      lineHeight: 1.25,
                                                                      overflow: 'hidden',
                                                                      textOverflow: 'ellipsis',
                                                                      whiteSpace: 'nowrap',
                                                                    }}
                                                                  >
                                                                    {row.title}
                                                                  </Typography>
                                                                  {row.isRecipe ? (
                                                                    <Tooltip title="View recipe">
                                                                      <IconButton
                                                                        size="small"
                                                                        onClick={(event) => {
                                                                          event.stopPropagation();
                                                                          handleViewBoardRecipe(day.day, entry.key, row, entry.slotOrder);
                                                                        }}
                                                                        onPointerDown={(event) => event.stopPropagation()}
                                                                        aria-label="View recipe details"
                                                                        sx={{
                                                                          flexShrink: 0,
                                                                          p: 0.15,
                                                                          color: '#98a2b3',
                                                                          '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.12)' },
                                                                        }}
                                                                      >
                                                                        <VisibilityIcon sx={{ fontSize: 17 }} />
                                                                      </IconButton>
                                                                    </Tooltip>
                                                                  ) : null}
                                                                </Box>
                                                                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#99a5b4', mt: 0.08 }}>
                                                                  {row.detailLabel ? `${row.detailLabel} · ` : null}
                                                                  <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                                                                    {row.caloriesLabel}
                                                                  </Box>
                                                                </Typography>
                                                                {!readOnly ? (
                                                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.65 }}>
                                                                    {row.optionGroupId ? (
                                                                      <Button
                                                                        size="small"
                                                                        disabled={isUpdatingThisItem || updatingGenericOptionItemId != null}
                                                                        onClick={() => {
                                                                          void handleUpdateGenericOptionGroup({
                                                                            entry,
                                                                            row,
                                                                            optionGroupId: null,
                                                                            optionGroupLabel: null,
                                                                          });
                                                                        }}
                                                                        sx={{ minWidth: 0, px: 0.7, py: 0.15, borderRadius: '8px', textTransform: 'none', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}
                                                                      >
                                                                        Remove from AND
                                                                      </Button>
                                                                    ) : (
                                                                      <Button
                                                                        size="small"
                                                                        disabled={isUpdatingThisItem || updatingGenericOptionItemId != null}
                                                                        onClick={() => {
                                                                          void handleUpdateGenericOptionGroup({
                                                                            entry,
                                                                            row,
                                                                            optionGroupId: activeGroupId,
                                                                            optionGroupLabel: activeGroupLabel,
                                                                            selectedOptionGroupId: activeGroupId,
                                                                          });
                                                                        }}
                                                                        sx={{ minWidth: 0, px: 0.7, py: 0.15, borderRadius: '8px', textTransform: 'none', fontSize: '0.7rem', fontWeight: 700, color: '#1d4ed8', bgcolor: '#eff6ff' }}
                                                                      >
                                                                        {selectedGenericGroupId || group.optionGroupId ? 'Add to AND' : 'Start AND option'}
                                                                      </Button>
                                                                    )}
                                                                  </Box>
                                                                ) : null}
                                                              </Box>
                                                            </Box>
                                                          </React.Fragment>
                                                        );
                                                      })}
                                                    </Stack>
                                                  </Box>
                                                );
                                              })}
                                            </Stack>
                                          ) : (
                                            <Stack spacing={1.35}>
                                              {rows.map((row, rowIndex) => (
                                                <Box key={`${entry.key}-row-${rowIndex}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                                  <Avatar
                                                    src={row.image}
                                                    alt={row.title}
                                                    variant="rounded"
                                                    sx={{ width: 34, height: 34, borderRadius: '8px', flexShrink: 0 }}
                                                    imgProps={{
                                                      onError: (event: React.SyntheticEvent<HTMLImageElement>) => {
                                                        const target = event.currentTarget;
                                                        target.onerror = null;
                                                        target.src = DEFAULT_RECIPE_IMAGE;
                                                      },
                                                    }}
                                                  />
                                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
                                                      <Typography
                                                        sx={{
                                                          flex: 1,
                                                          minWidth: 0,
                                                          fontFamily: 'Inter, sans-serif',
                                                          fontSize: '0.78rem',
                                                          fontWeight: 600,
                                                          color: '#344054',
                                                          lineHeight: 1.25,
                                                          overflow: 'hidden',
                                                          textOverflow: 'ellipsis',
                                                          whiteSpace: 'nowrap',
                                                        }}
                                                      >
                                                        {row.title}
                                                      </Typography>
                                                      {row.isRecipe ? (
                                                        <Tooltip title="View recipe">
                                                          <IconButton
                                                            size="small"
                                                            onClick={(event) => {
                                                              event.stopPropagation();
                                                              handleViewBoardRecipe(day.day, entry.key, row, entry.slotOrder);
                                                            }}
                                                            onPointerDown={(event) => event.stopPropagation()}
                                                            aria-label="View recipe details"
                                                            sx={{
                                                              flexShrink: 0,
                                                              p: 0.15,
                                                              color: '#98a2b3',
                                                              '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.12)' },
                                                            }}
                                                          >
                                                            <VisibilityIcon sx={{ fontSize: 17 }} />
                                                          </IconButton>
                                                        </Tooltip>
                                                      ) : null}
                                                    </Box>
                                                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#99a5b4', mt: 0.08 }}>
                                                      {row.detailLabel ? `${row.detailLabel} · ` : null}
                                                      <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                                                        {row.caloriesLabel}
                                                      </Box>
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              ))}
                                            </Stack>
                                          )
                                        ) : (
                                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#98a2b3' }}>
                                            No recipes added in this meal.
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>,
                                    185
                                  )}
                                </React.Fragment>
                              )}
                            </SortableMealWrapper>
                          );
                        }

                        return (
                          <SortableMealWrapper key={`collapsed-${entry.key}-${index}`} id={`meal-${day.day}-${entry.key}`} dayNumber={day.day} mealKey={entry.key} disabled={readOnly}>
                            {({ setNodeRef, attributes, listeners, style, isDragging }) => (
                              <React.Fragment>
                                {renderTimelineNode(
                                  <Box
                                    ref={setNodeRef}
                                    style={style}
                                    id={`meal-card-${day.day}-${entry.key}`}
                                    {...attributes}
                                    {...listeners}
                                    onClick={(e) => {
                                      // Don't toggle if we're dragging or if the click was part of a drag action
                                      if (!isDragging) {
                                        toggleMealRow(day.day, entry.key);
                                      }
                                    }}
                                    sx={{
                                      borderRadius: '9px',
                                      border: '1px solid #dbe3ea',
                                      bgcolor: '#fff',
                                      px: 0.85,
                                      py: 0.78,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 0.8,
                                      cursor: 'pointer',
                                      outline: 'none',
                                      position: 'relative',
                                      zIndex: isDragging ? 100 : 1,
                                      boxShadow: isDragging ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' : 'none',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                                      {!readOnly ? (
                                        <DragIndicatorRoundedIcon sx={{ fontSize: 16, opacity: 0.6, mr: 0.3 }} />
                                      ) : null}
                                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', fontWeight: 500, color: '#111827', whiteSpace: 'nowrap' }}>
                                        {entry.label}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 500, color: '#344054', whiteSpace: 'nowrap' }}>
                                        {entry.timeLabel}
                                      </Typography>
                                      {(canEditNotes || entry.notes?.trim()) ? (
                                        <Box
                                          onClick={(event) => event.stopPropagation()}
                                          onPointerDown={(event) => event.stopPropagation()}
                                        >
                                          <MealPlanNotesControl
                                            value={entry.notes}
                                            disabled={isSavingMealPlanNotes}
                                            editable={canEditNotes}
                                            variant="icon"
                                            size="sm"
                                            label="Meal note"
                                            emptyLabel="Add a note for this meal"
                                            helperText="Optional. Tips for this meal only."
                                            placeholder="Enter meal notes"
                                            onSave={async (notes) => {
                                              if (!onSaveMealPlanNotes) return false;
                                              return onSaveMealPlanNotes({
                                                level: 'meal',
                                                day: day.day,
                                                mealName: entry.mealName,
                                                notes,
                                              });
                                            }}
                                          />
                                        </Box>
                                      ) : null}
                                    </Box>
                                  </Box>,
                                  index === mealEntries.length - 1 ? 10 : 42
                                )}
                              </React.Fragment>
                            )}
                          </SortableMealWrapper>
                        );
                      })}
                    </Stack>
                  </SortableContext>
                </Box>

                <Box sx={{ px: 1.2, pb: 1.2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.95 }}>
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={() => onEditDay?.(day.day)}
                    sx={{
                      minHeight: 42,
                      borderRadius: '10px',
                      borderWidth: '1.5px',
                      borderStyle: 'solid',
                      borderColor: '#c3ccd6',
                      bgcolor: '#ffffff',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: '#374151',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#b8c3ce',
                        bgcolor: '#ffffff',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {editDayLabel ?? 'Edit'}
                  </Button>

                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => setAnalysisDayNumber(day.day)}
                    sx={{
                      minHeight: 42,
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      bgcolor: '#2ba8d1',
                      color: '#ffffff',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#259dc5',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Analyse
                  </Button>
                </Box>
              </Paper>
            )}
            </SortableDayWrapper>
            );
          })}

          {canShowAddDayUi ? (
            <Tooltip
              title={isMaxDaysReached ? "Maximum 7 days allowed in a plan" : "Add a new day to this plan"}
              placement="top"
              arrow
            >
              <Paper
                elevation={0}
                role={isMaxDaysReached ? "presentation" : "button"}
                tabIndex={isMaxDaysReached ? -1 : 0}
                onClick={handleAttemptAddDay}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    handleAttemptAddDay(event);
                  }
                }}
                sx={{
                  alignSelf: 'stretch',
                  borderRadius: '16px',
                  border: isMaxDaysReached ? '2px dashed #b5c7d8' : '2px solid #87d0ea',
                  bgcolor: isMaxDaysReached ? '#f8fafc' : '#f4f8fb',
                  minWidth: 140,
                  height: '100%',
                  display: 'grid',
                  alignContent: 'center',
                  justifyItems: 'center',
                  gap: 0.9,
                  color: isMaxDaysReached ? '#cbd5e1' : '#9eb0c0',
                  cursor: isMaxDaysReached ? 'not-allowed' : 'pointer',
                  transition: 'all 160ms ease',
                  opacity: isMaxDaysReached ? 0.6 : 1,
                  ...( !isMaxDaysReached && {
                    '&:hover': {
                      bgcolor: '#ecf6fb',
                      borderColor: '#66c4e5',
                      color: '#66c4e5'
                    },
                    '&:focus-visible': {
                      outline: '2px solid #2aabd7',
                      outlineOffset: 2,
                    },
                  }),
                }}
              >
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: '50%',
                    border: isMaxDaysReached ? '1px solid #cbd5e1' : '1px solid #96d8ef',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: isMaxDaysReached ? '#f1f5f9' : '#eef9fd',
                    transition: 'all 160ms ease',
                  }}
                >
                  <AddRoundedIcon sx={{ fontSize: 28, color: isMaxDaysReached ? '#94a3b8' : '#72c7e8' }} />
                </Box>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', fontWeight: 500 }}>
                  {isMaxDaysReached ? 'Max Days' : 'Add Day'}
                </Typography>
              </Paper>
            </Tooltip>
          ) : null}
                </Box>
              </Box>
      </SortableContext>

            <DragOverlay dropAnimation={null}>
              {activeId && activeType === 'Day' && activeDayData ? (
                <DayCardOverlay day={activeDayData} targetDaily={targetDaily} isDayExpanded={(key) => isMealExpanded(activeDayData.day, key)} />
              ) : activeId && activeType === 'Meal' && activeMealData ? (
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      borderRadius: '9px',
                      bgcolor: '#26a8d6',
                      border: 'none',
                      color: '#fff',
                      px: 0.9,
                      py: 0.72,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.8,
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                      <DragIndicatorRoundedIcon sx={{ fontSize: 16, opacity: 0.95 }} />
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.98rem', fontWeight: 500, color: '#fff', whiteSpace: 'nowrap' }}>
                        {activeMealData.label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65 }}>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 500, color: '#fff', whiteSpace: 'nowrap' }}>
                        {activeMealData.timeLabel}
                      </Typography>
                      <IconButton size="small" sx={{ p: 0.15, color: '#fff' }} disabled>
                        <KeyboardArrowUpRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </Paper>

      <SaveAsTemplateModal
        open={isSaveTemplateOpen}
        onClose={() => setIsSaveTemplateOpen(false)}
        dayLabel={planName || 'Meal Plan'}
        mealCount={templateMealSlotCount}
        totalKcal={templateTotalCalories}
        templateKind="meal_plan"
        summaryDetail={`${data.days.length || 1} day${(data.days.length || 1) === 1 ? '' : 's'} · ${templateMealSlotCount} meal slots`}
        onSave={handleSaveBoardTemplate}
      />

      {recipeViewSelection ? (
        <RecipeDetailsModal
          recipeId={recipeViewSelection.recipeId}
          recipeName={recipeViewSelection.recipeName}
          open
          onOpenChange={(open) => {
            if (!open) setRecipeViewSelection(null);
          }}
          draftId={isDraftPlan ? data.id : undefined}
          mealPlanId={isDraftPlan ? undefined : data.id}
          itemId={recipeViewSelection.itemId}
          day={recipeViewSelection.day}
          mealName={recipeViewSelection.mealName}
          mealOrder={recipeViewSelection.mealOrder}
          source={recipeViewSelection.source}
          initialDefaultServingId={recipeViewSelection.defaultServingId}
          initialDefaultServingGrams={recipeViewSelection.defaultServingGrams}
          initialSelectedServingId={recipeViewSelection.selectedServingId}
          initialSelectedServingGrams={recipeViewSelection.selectedServingGrams}
          initialServingSizes={recipeViewSelection.servingSizes}
          onClose={() => {
            void onMealPlanUpdated?.();
          }}
        />
      ) : null}

      <ClientDetailsV2AddDayDialog
        open={isAddDayDialogOpen}
        onClose={() => setIsAddDayDialogOpen(false)}
        defaultDayName={defaultDayName}
        copyFromOptions={copyFromOptions}
        onSubmit={handleSubmitAddDay}
      />

      {copyTargetDay !== null ? (
        <ClientDetailsV2CopyDayDialog
          open
          onClose={handleCloseCopyDayDialog}
          targetDayNumber={copyTargetDay}
          targetDayLabel={copyTargetDayLabel}
          copyFromOptions={copyFromOptionsForTarget}
          onSubmit={handleSubmitCopyDay}
          isSubmitting={isCopyingDay}
        />
      ) : null}
      <DeleteDayDialog
        open={pendingDeleteDayNumber !== null}
        isSubmitting={isDeletingDay}
        dayLabel={pendingDeleteDayNumber !== null ? `Day ${pendingDeleteDayNumber}` : 'this day'}
        onClose={handleCloseDeleteDayDialog}
        onConfirm={handleConfirmDeleteDay}
      />

      <Dialog
        open={isFinalizeValidationOpen}
        onClose={() => setIsFinalizeValidationOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 0.5 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.1,
            fontFamily: 'Inter, sans-serif',
            fontSize: '1.05rem',
            fontWeight: 700,
            color: '#344054',
            pb: 1,
          }}
        >
          Add meals before finalising
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: '#475467', mb: 1.4 }}>
            {emptyDays.length === 1
              ? 'The following day has no meals yet. Add at least one recipe to it, or remove the day, before finalising the plan.'
              : 'The following days have no meals yet. Add at least one recipe to each, or remove them, before finalising the plan.'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
            {emptyDays.map((entry) => (
              <Box
                key={entry.day}
                sx={{
                  px: 1.2,
                  py: 0.5,
                  borderRadius: '999px',
                  bgcolor: '#EAF7FC',
                  border: '1px solid #d5f4ff',
                  color: '#127EA5',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                }}
              >
                {entry.label}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.2, pt: 1 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => setIsFinalizeValidationOpen(false)}
            sx={{
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              borderRadius: '10px',
              bgcolor: '#2aabd7',
              '&:hover': { bgcolor: '#249cc5' },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={analysisDayNumber !== null}
        onClose={() => setAnalysisDayNumber(null)}
        ModalProps={{ keepMounted: true }}
        BackdropProps={{
          sx: {
            bgcolor: 'rgba(6, 16, 32, 0.56)',
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 560 },
            borderLeft: '1px solid #e5e7eb',
            bgcolor: '#f3f4f6',
            boxShadow: '-28px 0 44px rgba(15, 23, 42, 0.12)',
            overflowX: 'auto',
          },
        }}
      >
        {isAnalysisLoading ? (
          <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', bgcolor: '#f3f4f6' }}>
            <CircularProgress size={28} />
          </Box>
        ) : selectedAnalysis ? (
          <ManualMealPlanAnalysisDashboard
            selectedAnalysis={selectedAnalysis}
            targetDailyCalories={targetDaily.calories ?? 2000}
            onClose={() => setAnalysisDayNumber(null)}
            onViewDetailedAnalysis={client?.id ? handleViewDetailedAnalysisReport : undefined}
          />
        ) : null}
      </Drawer>
    </Box>
  );
};
